<script setup>
// =============================================================================
// FangateChatArea.vue — Stage E (Option C)
//
// Embeds Chatwoot's standalone <MessageList> (the props-driven Vue 3 component
// from `components-next/`, NOT the legacy MessagesView with its DOM-query
// lifecycle hooks). We own:
//   - Fangate-themed header (avatar, name, status, actions)
//   - Message fetching directly via MessageAPI
//   - A minimal reply box (textarea + send)
//
// What we get from Chatwoot:
//   - Bubble rendering, attachments, reactions, reply-to, status ticks,
//     edit/delete, copy-link (built into <Message>)
// =============================================================================

import { ref, computed, watch, nextTick, onMounted, onUnmounted, onErrorCaptured, reactive } from 'vue';
import { useStore } from 'vuex';
import MessageList from 'next/message/MessageList.vue';
import MessageAPI from 'dashboard/api/inbox/message';
import GiphyAPI from 'dashboard/api/giphy';
import ContactAPI from 'dashboard/api/contacts';
import TelegramActionsAPI from 'dashboard/api/telegramActions';
import FangateAPI from 'dashboard/api/fangate';
import TasksAPI from 'dashboard/api/captain/tasks';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useChatGptState } from 'dashboard/composables/useChatGptState';
import { emitter } from 'shared/helpers/mitt';
import { takeSale } from 'dashboard/helper/fangateSaleBus';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  /** Currently selected conversation id (null = empty state) */
  conversationId: { type: [Number, String], default: null },
  /** Conversation list from FangateListPane (so we can look up sender info) */
  conversations:  { type: Array, default: () => [] },
  /** All labels from Vuex (passed via Layout) — drives the "+ Label" picker */
  labels:         { type: Array, default: () => [] },
  /** Text to inject into the compose area (e.g. a Fangate product URL from
      the Fangate Panel's "Copy to chat"). Watched against externalTextNonce. */
  externalText:      { type: String, default: '' },
  externalTextNonce: { type: Number, default: 0 },
  /** Jump-to-message: when a search/reply hit asks us to scroll to a specific
      message. jumpMessageId is the target; jumpNonce re-triggers a jump even to
      the same message (mirrors the externalText/Nonce pattern). */
  jumpMessageId:     { type: [Number, String], default: null },
  jumpNonce:         { type: Number, default: 0 },
  /** Mobile shell → render a back arrow in the header that pops back to the list. */
  isMobile:          { type: Boolean, default: false },
});

const emit = defineEmits(['toggle-notes', 'labels-changed', 'back', 'open-fangate']);

const store = useStore();

// ---- Derived state ---------------------------------------------------------
const currentChat = computed(() => {
  if (!props.conversationId) return null;
  const id = Number(props.conversationId);
  return (props.conversations || []).find(c => Number(c.id) === id) ?? null;
});
const sender        = computed(() => currentChat.value?.meta?.sender ?? {});
const senderAttrs   = computed(() => sender.value?.additional_attributes ?? {});
// Telegram avatar thumbnails don't always load (CORS/ORB, expired URLs). On <img>
// error we fall back to the initials instead of the browser's broken-image + alt
// text (which rendered as "Sven" spilling out of the circle). Reset per
// conversation so the next contact's avatar is attempted fresh.
const avatarBroken  = ref(false);
watch(() => props.conversationId, () => { avatarBroken.value = false; });

// Inbox name to display under the chat header so the agent knows WHICH
// persona they're operating as. Pulled from Vuex via the standard
// inboxes/getInbox getter — same data that drives the chat-list header.
const personaInboxName = computed(() => {
  const id = currentChat.value?.inbox_id;
  if (!id) return '';
  const inbox = store.getters['inboxes/getInbox'](id);
  return inbox?.name?.trim() || '';
});

// Pretty-format "5 minutes ago" / "just now" for the Last-sync row in the
// contact-info panel. Times are stored ISO-8601 by RefreshContactProfileJob.
const formatLastSync = iso => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 30)   return 'just now';
  if (secs < 60)   return `${secs} s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  return new Date(t).toLocaleString();
};
// ---- Contact presence (MTProto user-account: online / last-seen) -----------
// Resolve presence at RENDER time by contact id (like FangateListPane) so a
// contact.updated that fires mid-switch is never lost (the old imperative id
// guard dropped those). Sources, freshest first: live map (EDIT_CONTACT pushes
// while open) → contacts store (kept fresh by contact.updated) → senderAttrs
// (snapshot at load). contact.updated does NOT refresh conv.meta.sender, so the
// snapshot alone goes stale — hence the first two live sources.
const presenceById  = reactive({});   // contactId -> { status, lastSeen }
const presence      = computed(() => {
  const id = sender.value?.id;
  const live = id != null ? presenceById[id] : null;
  if (live && live.status != null) return live;
  const c = id != null ? store.getters['contacts/getContact'](id) : null;
  const aa = (c && c.additional_attributes && c.additional_attributes.telegram_presence != null)
    ? c.additional_attributes
    : (senderAttrs.value || {});
  return { status: aa.telegram_presence ?? null, lastSeen: aa.telegram_last_seen ?? null };
});
const isOnline      = computed(() => presence.value.status === 'online');
const presenceText  = computed(() => {
  const p = presence.value;
  switch (p.status) {
    case 'online':     return 'Online';
    case 'recently':   return 'last seen recently';
    case 'last_week':  return 'last seen within a week';
    case 'last_month': return 'last seen within a month';
    case 'offline':    return p.lastSeen ? `last seen ${dynamicTime(p.lastSeen)}` : 'Offline';
    default:           return 'Offline';
  }
});
const inboxId       = computed(() => currentChat.value?.inbox_id ?? null);
const currentUserId = computed(() => store.getters.getCurrentUserID);

// ===== Fangate Live-Sale pop-up (dismissible) ==============================
// A successful sale broadcasts `fangate.sale`; show a one-off dismissible nudge
// on the active chat of the SELLING inbox. If no chat of that inbox is open when
// it arrives, the sale is buffered (fangateSaleBus) and surfaces on the first
// such chat opened next. Switching chats clears the current pop-up and shows any
// buffered sale for the new inbox.
const salePopup = ref(null);
const saleMoney = computed(() => {
  const s = salePopup.value?.sale;
  if (!s) return '';
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: s.currency || 'EUR' }).format(Number(s.gross || 0));
  } catch (_) {
    return `${s.currency || 'EUR'} ${Number(s.gross || 0).toFixed(2)}`;
  }
});
const saleProduct = computed(() => {
  const s = salePopup.value?.sale;
  if (!s) return '';
  return s.product_name || (s.product_id ? `#${s.product_id}` : '');
});
const dismissSale = () => { salePopup.value = null; };
// "Assign this buyer to a Chat" (pop-up) → ask FangatePanel to open the
// assign-email-to-chat modal pre-filled with this buyer's email, then dismiss.
const assignSaleBuyer = () => {
  const s = salePopup.value?.sale;
  if (!s?.buyer_email) return;
  emitter.emit('fangate:assign-buyer', { email: s.buyer_email, inbox_id: salePopup.value?.inbox_id });
  salePopup.value = null;
};
const onFangateSalePush = payload => {
  if (Number(payload?.inbox_id) === Number(inboxId.value)) {
    takeSale(payload.inbox_id);   // consume the buffered copy
    salePopup.value = payload;
  }
  // else: leave it buffered for when that inbox's chat is opened
};
watch(inboxId, (id, prev) => {
  if (id === prev) return;
  salePopup.value = null;                  // drop any pop-up from the previous chat
  const buffered = takeSale(id);
  if (buffered) salePopup.value = buffered;
});
onMounted(() => {
  emitter.on('fangate:sale', onFangateSalePush);
  const buffered = takeSale(inboxId.value);
  if (buffered) salePopup.value = buffered;
});
onUnmounted(() => emitter.off('fangate:sale', onFangateSalePush));

// MTProto (user-account) chats backfill their history ON DEMAND from Telegram
// (the DB only holds what's been imported so far): on open we pull the recent
// 30, and scroll-up pulls 60 older once the DB is exhausted.
const isMtprotoInbox = computed(() =>
  store.getters['inboxes/getInbox'](inboxId.value)?.channel_type === 'Channel::TelegramUser'
);

// #126 — "new account" safety reminder. Shown in the chat background while a
// freshly-connected MTProto account is < 7 days old (account_connected_at from
// the inbox serializer; reconnect does NOT reset it). Dismissible per inbox,
// persisted to localStorage. Pure reminder — NOT a sending restriction.
const NEW_ACCOUNT_DAYS = 7;
const NEWACCT_DISMISS_KEY = 'fca-newacct-hint-dismissed';
const loadDismissedNewAcct = () => {
  try { return new Set(JSON.parse(localStorage.getItem(NEWACCT_DISMISS_KEY) || '[]')); }
  catch (_) { return new Set(); }
};
const dismissedNewAcct = ref(loadDismissedNewAcct());

const newAccountInbox = computed(() => {
  const id = currentChat.value?.inbox_id;
  if (!id) return null;
  const inbox = store.getters['inboxes/getInbox'](id);
  if (inbox?.channel_type !== 'Channel::TelegramUser') return null;
  const connectedAt = inbox?.account_connected_at; // unix seconds
  if (!connectedAt) return null;
  const ageMs = Date.now() - connectedAt * 1000;
  return ageMs < NEW_ACCOUNT_DAYS * 86400 * 1000 ? inbox : null;
});

const showNewAccountHint = computed(
  () => !!newAccountInbox.value && !dismissedNewAcct.value.has(newAccountInbox.value.id)
);

const dismissNewAccountHint = () => {
  const inbox = newAccountInbox.value;
  if (!inbox) return;
  const next = new Set(dismissedNewAcct.value);
  next.add(inbox.id);
  dismissedNewAcct.value = next; // reassign so the computed re-evaluates
  try { localStorage.setItem(NEWACCT_DISMISS_KEY, JSON.stringify([...next])); } catch (_) {}
};

const messages         = ref([]);
const isLoading        = ref(false);
const isLoadingOlder   = ref(false);
const allLoaded        = ref(false);  // true → no older messages on server
const pendingJumpId    = ref(null);   // message id we still owe a scroll-to
const sendingText      = ref('');
const isSending        = ref(false);
// Reply-to context. The bubble action menu fires a global bus event with
// the target message; we capture it here, show a banner above the composer,
// and thread it via content_attributes.in_reply_to on send (the backend's
// ensure_in_reply_to callback resolves it to the Telegram reply id).
const replyToMessage   = ref(null);
const onToggleReplyTo = msg => {
  // Only honour the event for the conversation we're currently showing.
  if (!msg || Number(msg.conversationId ?? msg.conversation_id) !== Number(props.conversationId)) return;
  replyToMessage.value = msg;
};
const cancelReply = () => { replyToMessage.value = null; };

// Telegram inline-edit context (#173). When the bubble's "Edit" action fires
// START_TELEGRAM_EDIT we hydrate the composer with the existing text and
// flip a flag so sendMessage routes through TelegramActionsAPI.editMessage
// instead of MessageAPI.create. Cancelling clears everything back to a
// fresh-compose state.
const editingMessage = ref(null);
const editingDraftBackup = ref(null);
const onStartEdit = msg => {
  if (!msg || Number(msg.conversation_id ?? msg.conversationId) !== Number(props.conversationId)) return;
  // Reply and Edit are mutually exclusive — never both visible.
  replyToMessage.value = null;
  // Save the in-flight draft so it can be restored on Cancel.
  editingDraftBackup.value = sendingText.value;
  editingMessage.value = msg;
  sendingText.value = msg.content || '';
  // Focus the textarea + park the caret at the end so the agent can
  // start typing immediately. nextTick because the textarea is gated
  // behind v-model reactivity that hasn't propagated this frame yet.
  nextTick(() => {
    const el = composerTextareaRef.value;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try { el.setSelectionRange(len, len); } catch (_) { /* old browsers */ }
  });
};
const cancelEdit = () => {
  if (editingDraftBackup.value !== null) {
    sendingText.value = editingDraftBackup.value;
    editingDraftBackup.value = null;
  } else {
    sendingText.value = '';
  }
  editingMessage.value = null;
};
const scrollerRef      = ref(null);
const composerTextareaRef = ref(null);
const fileInputRef     = ref(null);
const voiceInputRef    = ref(null);
const pendingFiles     = ref([]);   // File[] selected but not yet sent

// ---- Compose extras --------------------------------------------------------
const MAX_CHARS        = 4096;
const showEmojiPicker  = ref(false);
const emojiTab         = ref('emoji');           // 'emoji' | 'gif'
const aiSuggestions    = ref([]);                // generated reply variants
const isGeneratingAI   = ref(false);

// ChatGPT "Suggest reply" is gated on the integration being connected + active
// in Settings → Automations. No key connected there → the button stays hidden.
const { isChatGptActive } = useChatGptState();

const charCount        = computed(() => sendingText.value.length);
const showAiSuggest    = computed(() =>
  isChatGptActive.value && !sendingText.value.trim() && !pendingFiles.value.length
);

// Quick-pick emoji palette (deferred GIF integration)
const EMOJI_LIST = [
  '😀','😂','🥰','😍','😘','😎','🤩','🥳',
  '😉','😊','🙃','😋','😜','🤔','🙄','😴',
  '🥺','😭','🤯','😤','🔥','✨','💫','⭐',
  '❤️','💔','💕','💯','👍','👎','🙏','🤝',
  '🎉','🎁','💝','🌹','🌺','🌸','☀️','🌙',
];

const insertEmoji = emo => {
  sendingText.value = (sendingText.value || '') + emo;
  showEmojiPicker.value = false;
};

const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};
const closeEmojiPicker = () => { showEmojiPicker.value = false; };

// ---- Insert-link popover (URL toolbar button) -----------------------------
// Builds a markdown link [text](url) and drops it into the composer at the
// caret. On send it renders as a real clickable link BOTH in-chat
// (FormattedContent renders the markdown) AND for the Telegram recipient — no
// send-path change needed: the Bot path converts markdown→HTML (<a href>), and
// the MTProto path sends the raw content which Telethon parses (its default
// parse_mode is markdown). 'Channel::TelegramUser' is intentionally absent from
// MarkdownRendererService's map, so outgoing_content stays raw for the sidecar.
const showLinkPopover = ref(false);
const linkText = ref('');
const linkUrl  = ref('');
const toggleLinkPopover = () => {
  showLinkPopover.value = !showLinkPopover.value;
  if (showLinkPopover.value) showEmojiPicker.value = false;   // one popover at a time
};
const insertLink = () => {
  let url = linkUrl.value.trim();
  if (!url) return;
  // Default to https:// when the agent omits the scheme, else it reads as relative.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) url = `https://${url}`;
  // A ')' in the URL would prematurely close the markdown link — encode parens.
  url = url.replace(/\(/g, '%28').replace(/\)/g, '%29');
  // Visible label defaults to the URL; strip brackets that would break [..](..).
  const label = (linkText.value.trim() || url).replace(/[[\]]/g, '');
  const md = `[${label}](${url})`;
  const el = composerTextareaRef.value;
  const cur = sendingText.value || '';
  if (el && typeof el.selectionStart === 'number') {
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    sendingText.value = cur.slice(0, start) + md + cur.slice(end);
    nextTick(() => {
      el.focus();
      const pos = start + md.length;
      try { el.setSelectionRange(pos, pos); } catch (_) { /* old browsers */ }
    });
  } else {
    sendingText.value = cur + md;
  }
  linkText.value = '';
  linkUrl.value  = '';
  showLinkPopover.value = false;
};

// ---- GIF picker (Giphy via same-origin proxy; key stays server-side) -------
const gifQuery   = ref('');
const gifResults = ref([]);
const gifLoading = ref(false);
const gifError   = ref('');
let gifTimer = null;
let gifLoadedOnce = false;
const loadGifs = async () => {
  gifLoading.value = true;
  gifError.value   = '';
  try {
    const q = gifQuery.value.trim();
    gifResults.value = q ? await GiphyAPI.search(q) : await GiphyAPI.trending();
    gifLoadedOnce = true;
  } catch (e) {
    gifResults.value = [];
    gifError.value = e?.response?.data?.error || 'GIF search is unavailable.';
  } finally {
    gifLoading.value = false;
  }
};
const onGifSearch = () => {
  if (gifTimer) clearTimeout(gifTimer);
  gifTimer = setTimeout(loadGifs, 350);
};
// Lazy-load trending the first time the GIF tab is opened.
watch([showEmojiPicker, emojiTab], ([open, tab]) => {
  if (open && tab === 'gif' && !gifLoadedOnce) loadGifs();
});
// Send a GIF as a normal file attachment — fetch the bytes via our proxy with
// the app's axios (carries auth; a raw fetch() would 401), wrap in a File, and
// reuse the message pipeline. Telegram renders a .gif document as an animation.
const sendGif = async g => {
  if (!props.conversationId || !g?.gif) return;
  showEmojiPicker.value = false;
  try {
    const blob = await GiphyAPI.fetchGifBlob(g.gif);
    const file = new File([blob], `giphy-${g.id || 'gif'}.gif`, { type: blob.type || 'image/gif' });
    const msg = await MessageAPI.create({
      conversationId: props.conversationId,
      message: '',
      files: [file],
      private: false,
    });
    if (msg?.data) upsertMessage(msg.data);
    scrollToBottom();
  } catch (e) {
    childError.value = { message: 'Failed to send GIF: ' + (e?.message ?? e) };
  }
};

const triggerVoice = () => voiceInputRef.value?.click();
const onVoiceFile = e => {
  const file = e.target.files?.[0];
  if (file) {
    // Tag the file for the voice-tinted chip in the attachment row.
    // Backend routing now lives in Telegram::SendAttachmentsService —
    // ANY audio attachment (whether picked via "Voice" or via "Add file")
    // unconditionally goes through /sendVoice, so this flag is purely
    // cosmetic at this point (controls the purple chip styling +
    // mic-icon vs paperclip-icon in the pending-attachment list).
    try { Object.defineProperty(file, '_isVoice', { value: true }); } catch (_) {}
    pendingFiles.value = [...pendingFiles.value, file];
  }
  e.target.value = '';
};

// AI Suggest — real ChatGPT reply suggestions via Chatwoot's Captain task API.
// (props.conversationId IS the conversation display_id the endpoints expect.)
// Two-step so the three cards are guaranteed-distinct rather than three near-
// identical drafts:
//   1) POST /captain/tasks/reply_suggestion → one base draft from the history
//      (reads the OpenAI key from the account's `openai` integration hook).
//   2) POST /captain/tasks/rewrite for each tone → three tone variants.
// Clicking a card drops its text into the composer to edit + send. Falls back
// to the base draft if every rewrite fails.
const SUGGEST_TONES = [
  { key: 'casual',    label: 'Casual' },
  { key: 'friendly',  label: 'Friendly' },
  { key: 'confident', label: 'Confident' },
];
const generateAiReply = async () => {
  if (isGeneratingAI.value || !props.conversationId) return;
  isGeneratingAI.value = true;
  aiSuggestions.value  = [];
  try {
    const { data: base } = await TasksAPI.replySuggestion(props.conversationId);
    if (base?.error) { childError.value = { message: base.error }; return; }
    const baseText = base?.message;
    if (!baseText) {
      childError.value = { message: 'ChatGPT returned no suggestion — try again.' };
      return;
    }
    const results = await Promise.allSettled(
      SUGGEST_TONES.map(t =>
        TasksAPI.rewrite({ content: baseText, operation: t.key, conversationId: props.conversationId })
      )
    );
    const cards = [];
    results.forEach((r, i) => {
      const text = r.status === 'fulfilled' ? r.value?.data?.message : null;
      if (text && !r.value?.data?.error) cards.push({ tone: SUGGEST_TONES[i].label, text });
    });
    aiSuggestions.value = cards.length ? cards : [{ tone: 'ChatGPT', text: baseText }];
  } catch (e) {
    childError.value = {
      message: e?.response?.data?.error || e?.message || 'Reply suggestion failed.',
    };
  } finally {
    isGeneratingAI.value = false;
  }
};
const useAiSuggestion = s => {
  sendingText.value = s.text;
  aiSuggestions.value = [];
};
const dismissSuggestions = () => { aiSuggestions.value = []; };

// ---- Error capture so a crashing bubble doesn't white-screen the column ----
const childError = ref(null);
onErrorCaptured((err, _instance, info) => {
  // eslint-disable-next-line no-console
  console.error('[FangateChatArea]', info, err);
  childError.value = { message: err?.message ?? String(err), info };
  return false;
});

// ---- Helpers ---------------------------------------------------------------
const getInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
};

const scrollToBottom = () => {
  nextTick(() => {
    const el = scrollerRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
};

// ---- Load messages whenever the conversation changes -----------------------
// Per-conversation message cache (in-memory, session-lived) so re-opening a
// chat is instant: show the cached messages, then revalidate from the DB in the
// background. MTProto history is pulled from Telegram only ONCE per chat (and
// only when the DB is sparse), in the background — not on every open, which is
// what used to make switching chats feel slow.
const messageCache       = new Map();
const historyLoadedConvs = new Set();

// LRU-cap the message cache so a long session that opens many chats can't grow
// memory unbounded. Map keeps insertion order; re-inserting on write moves a
// chat to "most recently used", so we always evict the oldest-touched one.
const MESSAGE_CACHE_MAX = 30;
const cacheMessages = (id, payload) => {
  messageCache.delete(id);
  messageCache.set(id, payload);
  while (messageCache.size > MESSAGE_CACHE_MAX) {
    messageCache.delete(messageCache.keys().next().value);
  }
};

const loadMessages = async id => {
  if (!id) { messages.value = []; return; }
  childError.value = null;
  pendingJumpId.value = null;
  allLoaded.value = false;

  // Instant paint from cache when we've shown this chat before.
  const cached = messageCache.get(id);
  if (cached) {
    messages.value = cached;
    isLoading.value = false;
    nextTick(scrollToBottom);
  } else {
    messages.value = [];
    isLoading.value = true;
  }

  try {
    // Read the DB first (fast, local) and paint immediately.
    const resp = await MessageAPI.getPreviousMessages({ conversationId: id });
    if (Number(id) !== Number(props.conversationId)) return;   // switched away mid-flight
    const payload = resp?.data?.payload ?? [];
    messages.value = payload;
    cacheMessages(id, payload);
    if (payload.length < 20 && !isMtprotoInbox.value) allLoaded.value = true;
    markBusinessMessagesRead(payload);
    // MTProto: pull the contact's CURRENT online status on open (the live event
    // only fires on change) → flows back via the presence path. Fire-and-forget.
    if (isMtprotoInbox.value) TelegramActionsAPI.fetchPresence(id).catch(() => {});

    // MTProto: if this chat still only holds the connect-time preview (sparse),
    // pull the recent 30 from Telegram in the BACKGROUND — once per chat — then
    // refresh. Opening never blocks on a Telegram round-trip anymore.
    if (isMtprotoInbox.value && !historyLoadedConvs.has(id) && payload.length < 20) {
      historyLoadedConvs.add(id);
      backfillRecentHistory(id);
    }
  } catch (e) {
    if (!cached) {
      childError.value = { message: 'Failed to load messages: ' + (e?.message ?? e) };
      messages.value = [];
    }
  } finally {
    isLoading.value = false;
    if (pendingJumpId.value) goToMessage(pendingJumpId.value);
    else scrollToBottom();
  }
};

// Background-only: pull the recent 30 from Telegram for a sparse MTProto chat,
// then re-read the DB if we're still on that conversation.
const backfillRecentHistory = async id => {
  try {
    const { data } = await TelegramActionsAPI.loadMtprotoHistory(id, { limit: 30 });
    if (!data?.ingested || Number(id) !== Number(props.conversationId)) return;
    const resp = await MessageAPI.getPreviousMessages({ conversationId: id });
    if (Number(id) !== Number(props.conversationId)) return;
    const payload = resp?.data?.payload ?? [];
    messages.value = payload;
    cacheMessages(id, payload);
    markBusinessMessagesRead(payload);
    nextTick(scrollToBottom);
  } catch (_) { /* sidecar unavailable → keep the DB view */ }
};

// Send the incoming-message source_ids to /telegram_actions/read_business
// so Telegram marks them as read on the sender side. Bot-API only —
// MTProto (Channel::TelegramUser) uses a different read mechanism and the
// endpoint 422s on it; skip the call entirely for MTProto inboxes.
const markBusinessMessagesRead = msgs => {
  const inbox = store.getters['inboxes/getInbox'](currentChat.value?.inbox_id);
  if (inbox?.channel_type !== 'Channel::Telegram') return;
  const ids = (msgs || [])
    .filter(m => m.message_type === 0 && m.source_id)  // 0 = incoming
    .map(m => m.source_id);
  if (!ids.length) return;
  TelegramActionsAPI.readBusiness(props.conversationId, ids).catch(() => {});
};

// Read older messages straight from the DB (keyset-paginated before `beforeId`).
const fetchOlderFromDb = async beforeId => {
  const resp = await MessageAPI.getPreviousMessages({
    conversationId: props.conversationId,
    before: beforeId,
  });
  return resp?.data?.payload ?? [];
};

// ---- Pagination: load older when user scrolls near top ---------------------
const loadOlderMessages = async () => {
  if (isLoadingOlder.value || allLoaded.value || !props.conversationId) return;
  if (!messages.value.length) return;
  const oldestId = messages.value[0].id;
  if (!oldestId) return;
  isLoadingOlder.value = true;
  const scroller = scrollerRef.value;
  const prevScrollHeight = scroller?.scrollHeight ?? 0;
  try {
    let older = await fetchOlderFromDb(oldestId);

    // MTProto: the DB only holds imported history. When it's exhausted, pull
    // the next 60 older messages from Telegram (keyed on the oldest loaded
    // Telegram message id), then re-read the DB to surface them.
    if (!older.length && isMtprotoInbox.value) {
      const beforeSrc = messages.value[0].source_id;
      if (beforeSrc) {
        try {
          const { data } = await TelegramActionsAPI.loadMtprotoHistory(props.conversationId, {
            limit: 60,
            before: beforeSrc,
          });
          if (data?.ingested > 0) older = await fetchOlderFromDb(oldestId);
        } catch (_) { /* sidecar unavailable → treat as end of history */ }
      }
    }

    // Prepend any genuinely new (unseen) messages.
    const seen = new Set(messages.value.map(m => m.id));
    const fresh = older.filter(m => !seen.has(m.id));
    if (!fresh.length) {
      allLoaded.value = true;
    } else {
      messages.value = [...fresh, ...messages.value];
      // Non-MTProto: a short page means we hit the start. MTProto decides via
      // the Telegram probe above, so never shortcut it here.
      if (older.length < 20 && !isMtprotoInbox.value) allLoaded.value = true;
      // Preserve scroll position so prepending feels seamless
      nextTick(() => {
        if (scroller) {
          scroller.scrollTop = scroller.scrollHeight - prevScrollHeight;
        }
      });
    }
  } catch (e) {
    childError.value = { message: 'Failed to load older messages: ' + (e?.message ?? e) };
  } finally {
    isLoadingOlder.value = false;
  }
};

const onScroll = e => {
  // Trigger when within 80px of the top
  if (e.target.scrollTop < 80) loadOlderMessages();
};

// ---- Jump to a specific message (search hit / reply-context click) ----------
// The rendered row carries id="message{id}" (components-next Message.vue). We
// centre it in our scroller manually (deterministic for a nested scroll
// container — scrollIntoView can misbehave / pick the wrong ancestor) and flash
// it. .fca-flash is styled in an unscoped block below because the row lives
// inside the child <MessageList>.
// Centre the target row in the scroller. Manual scrollTop math (deterministic
// for a nested scroll container). `flash` plays the highlight once; re-pins pass
// flash:false so they don't re-trigger the animation.
const centerMessage = (messageId, { flash = false } = {}) => {
  const root = scrollerRef.value;
  if (!root) return false;
  const el = root.querySelector(`#message${messageId}`);
  if (!el) return false;
  const elRect   = el.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  const delta = (elRect.top - rootRect.top) - (root.clientHeight - elRect.height) / 2;
  root.scrollTop = Math.max(0, root.scrollTop + delta);
  if (flash) {
    el.classList.remove('fca-flash');
    el.getBoundingClientRect(); // force reflow so a repeat jump restarts the flash
    el.classList.add('fca-flash');
    setTimeout(() => el.classList.remove('fca-flash'), 2200);
  }
  return true;
};

const scrollToHighlight = messageId => centerMessage(messageId, { flash: true });

const messageInData = id => messages.value.some(m => Number(m.id) === id);

// jumpToken supersedes an in-flight jump if the user switches conversations or
// clicks another search hit mid-paging, so a stale loop can't hijack the view.
let jumpToken = 0;
const goToMessage = async messageId => {
  const id = Number(messageId);
  if (!id || !props.conversationId) { pendingJumpId.value = null; return; }
  const myToken = ++jumpToken;
  // 1) Make sure the target is in our DATA. Page older (keeps the list
  //    contiguous) until it loads or we reach the start of history. Bounded so
  //    a bad/old id can't spin forever; ~12 pages ≈ 240 messages back.
  let pages = 0;
  while (!messageInData(id) && !allLoaded.value && pages < 12) {
    if (myToken !== jumpToken) return;
    await loadOlderMessages();
    pages += 1;
  }
  if (myToken !== jumpToken) return;
  if (!messageInData(id)) { pendingJumpId.value = null; return; } // truly absent
  // 2) It's in the data → the row may still be rendering after the switch/load.
  //    Retry the DOM scroll a few frames before giving up.
  for (let i = 0; i < 12; i += 1) {
    if (myToken !== jumpToken) return;
    await nextTick();
    if (scrollToHighlight(id)) {
      pendingJumpId.value = null;
      // Re-pin against late layout shifts (images/GIFs loading above the target
      // grow the content and push it out of view) until the view settles.
      // Token-guarded so a newer jump / conversation switch cancels these.
      [80, 200, 450, 800].forEach(ms => setTimeout(() => {
        if (myToken === jumpToken) centerMessage(id);
      }, ms));
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 30));
  }
  pendingJumpId.value = null;
};

// Entry point for both the search-hit prop and the reply-context bus event.
const requestJump = messageId => {
  const id = Number(messageId);
  if (!id) return;
  pendingJumpId.value = id;
  // Messages already loaded for this conversation → jump now. Otherwise the
  // initial loadMessages() will pick up pendingJumpId in its finally block.
  if (!isLoading.value && messages.value.length) goToMessage(id);
};

watch(
  () => props.conversationId,
  newId => { loadMessages(newId); },
  { immediate: true }
);

// Search hit asks us to scroll to a specific message (nonce re-triggers even for
// the same id / same already-open conversation).
watch(
  () => props.jumpNonce,
  () => requestJump(props.jumpMessageId)
);

// Reply-context preview click (components-next Base.vue emits SCROLL_TO_MESSAGE).
// Nothing in our shell listened before, so reply-jump was a no-op until now.
const onScrollToMessage = ({ messageId } = {}) => {
  if (messageId) requestJump(messageId);
};

// Once per browser session per contact: ask the backend to re-pull profile
// info (name, username, avatar) from Telegram. After that, sessionStorage
// remembers the contact id so we don't hammer Telegram on every chat-open
// the agent does during the same session. Cleared automatically when the
// browser tab closes — next login does a fresh refresh.
//
// Watched on sender.id (not conversationId) so we wait for the conversation
// row to actually arrive in Vuex before firing — opening a chat by URL
// before the conversation list has loaded would otherwise miss the refresh.
//
// Server-side cooldown (60s in RefreshContactProfileJob) is the secondary
// safety net in case multiple tabs open the same chat near-simultaneously.
const TG_REFRESH_SESSION_KEY = id => `fangate_tg_contact_refreshed_${id}`;
watch(
  () => sender.value?.id,
  contactId => {
    if (!contactId) return;
    const key = TG_REFRESH_SESSION_KEY(contactId);
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, String(Date.now()));
    ContactAPI.refreshTelegramProfile(contactId).catch(() => {
      // Silently swallow — the contact still renders with whatever's
      // already cached, and the next session-open will retry.
      sessionStorage.removeItem(key);
    });
  },
  { immediate: true }
);

// When the Fangate panel emits "Copy to chat", `externalTextNonce` bumps —
// append the text to the compose area (separated by space if there's existing
// content). Watching the nonce instead of the string lets the same text be
// sent multiple times in a row.
watch(
  () => props.externalTextNonce,
  () => {
    const t = props.externalText;
    if (!t) return;
    sendingText.value = sendingText.value
      ? sendingText.value + ' ' + t
      : t;
  }
);

// ---- Live updates via Action Cable -----------------------------------------
// Chatwoot's ActionCableConnector (initialised in App.vue) dispatches
// `addMessage` for every `message.created` event, which commits the
// ADD_MESSAGE mutation. We subscribe to that mutation, filter by the
// currently selected conversation, and append to our local `messages` list.
//
// Also covers SEND_MESSAGE-like mutations (UPDATE_MESSAGE, etc.) so status
// ticks update live when delivered/read receipts come in.
let unsubscribe = null;

const isForCurrent = msg =>
  msg && Number(msg.conversation_id) === Number(props.conversationId);

const upsertMessage = msg => {
  const idx = messages.value.findIndex(m => m.id === msg.id);
  if (idx === -1) {
    // Only follow a new arrival to the bottom if the user is already there.
    // Otherwise it would yank them off whatever they're reading — including a
    // search/reply jump they just landed on.
    const el = scrollerRef.value;
    const nearBottom = !el || (el.scrollHeight - el.scrollTop - el.clientHeight < 120);
    messages.value = [...messages.value, msg];
    if (nearBottom) scrollToBottom();
  } else {
    // Replace in place — preserve order
    const next = [...messages.value];
    next[idx] = { ...next[idx], ...msg };
    messages.value = next;
  }
};

// Mobile recovery: a backgrounded tab / locked phone can drop the websocket and
// MISS live `message.updated` events — most visibly a read-receipt (✓✓) set
// while we were away. ActionCable does not replay missed events, and the global
// reconnect re-sync keys off the route's conversation_id (which the Fangate UI
// doesn't set), so the open chat wouldn't recover on its own. When the tab
// returns to the foreground (or the socket reconnects) we silently re-fetch the
// recent messages and upsert them in place — refreshing statuses (and catching
// any missed messages) WITHOUT clearing the view or forcing a scroll.
const resyncMessages = async () => {
  const id = props.conversationId;
  if (!id || document.visibilityState === 'hidden') return;
  try {
    const resp = await MessageAPI.getPreviousMessages({ conversationId: id });
    if (Number(id) !== Number(props.conversationId)) return;   // switched away mid-flight
    (resp?.data?.payload ?? []).forEach(upsertMessage);
  } catch (_) { /* keep the current view on failure */ }
};
const onVisibilityResync = () => {
  if (document.visibilityState === 'visible') resyncMessages();
};

onMounted(() => {
  unsubscribe = store.subscribe(mutation => {
    if (!props.conversationId) return;
    const m = mutation?.payload;
    switch (mutation?.type) {
      case 'ADD_MESSAGE':
      case 'UPDATE_MESSAGE':
        if (isForCurrent(m)) upsertMessage(m);
        break;
      case 'contacts/EDIT_CONTACT':
        // A contact.updated arrived (e.g. presence change). Store by id — the
        // `presence` computed resolves the current chat's contact at render time,
        // so we never miss an update due to mid-switch timing.
        if (m && m.id != null) {
          const a = m.additional_attributes || {};
          presenceById[m.id] = {
            status: a.telegram_presence ?? null,
            lastSeen: a.telegram_last_seen ?? null,
          };
        }
        break;
      default:
        // ignore
    }
  });
  // Bubble "Reply" action → set the reply-to banner above the composer.
  emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, onToggleReplyTo);
  // Bubble "Edit" action → hydrate composer with existing content + flip
  // sendMessage into edit mode. See onStartEdit / cancelEdit above.
  emitter.on(BUS_EVENTS.START_TELEGRAM_EDIT, onStartEdit);
  // Reply-context preview click → scroll to the quoted message.
  emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, onScrollToMessage);
  // Mobile recovery: catch up on updates (e.g. read-receipts) missed while the
  // tab was backgrounded / the socket was down. See resyncMessages.
  document.addEventListener('visibilitychange', onVisibilityResync);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, resyncMessages);
});

onUnmounted(() => {
  if (typeof unsubscribe === 'function') unsubscribe();
  emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, onToggleReplyTo);
  emitter.off(BUS_EVENTS.START_TELEGRAM_EDIT, onStartEdit);
  emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, onScrollToMessage);
  document.removeEventListener('visibilitychange', onVisibilityResync);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, resyncMessages);
});

// Switching conversations clears any half-set reply / edit context.
watch(() => props.conversationId, () => {
  replyToMessage.value = null;
  editingMessage.value = null;
  editingDraftBackup.value = null;
});

// ---- Attachments -----------------------------------------------------------
const onFilePick = e => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;
  // 40 MB per file limit (Chatwoot's default)
  const tooBig = files.filter(f => f.size > 40 * 1024 * 1024);
  if (tooBig.length) {
    childError.value = { message: `File too large: ${tooBig[0].name} (max 40 MB)` };
    return;
  }
  pendingFiles.value = [...pendingFiles.value, ...files];
  // Clear input so picking the same file again still fires change
  e.target.value = '';
};
const removeFile = idx => {
  pendingFiles.value = pendingFiles.value.filter((_, i) => i !== idx);
};
const pickFiles = () => fileInputRef.value?.click();

// ---- Send a new message ----------------------------------------------------
const sendMessage = async () => {
  // Branch off into Telegram edit mode if the user opened a bubble via the
  // chevron "Edit" action — the composer is hosting an existing message
  // rather than drafting a new one, so we PATCH via TelegramActionsAPI
  // instead of POSTing a fresh message.
  if (editingMessage.value) {
    await submitEdit();
    return;
  }

  const text  = sendingText.value.trim();
  const files = pendingFiles.value;
  if ((!text && !files.length) || !props.conversationId || isSending.value) return;
  isSending.value = true;
  // Optimistic: clear textarea + files immediately
  const restoreText  = text;
  const restoreFiles = files;
  sendingText.value  = '';
  pendingFiles.value = [];
  // Successful send → kill the persisted draft so it doesn't reappear.
  // (If the send fails below, we restore the text into sendingText —
  // the next keystroke watcher will re-save the draft automatically.)
  clearCurrentDraft();
  // Capture + clear the reply context up front so the banner disappears
  // immediately; restored only if the send fails.
  const replyTarget = replyToMessage.value;
  replyToMessage.value = null;
  try {
    const payload = {
      conversationId: props.conversationId,
      message: text,
      files,
      private: false,
    };
    // Thread the reply via content_attributes.in_reply_to (Chatwoot message
    // id). The backend's ensure_in_reply_to callback resolves it to the
    // Telegram reply id, which channel.rb passes as reply_to_message_id.
    if (replyTarget?.id) {
      payload.contentAttributes = { in_reply_to: replyTarget.id };
    }
    const resp = await MessageAPI.create(payload);
    if (resp?.data) upsertMessage(resp.data);
    scrollToBottom();
  } catch (e) {
    sendingText.value  = restoreText;   // restore on failure
    pendingFiles.value = restoreFiles;
    replyToMessage.value = replyTarget; // restore reply banner too
    childError.value = { message: 'Failed to send: ' + (e?.message ?? e) };
  } finally {
    isSending.value = false;
  }
};

// ---- Submit an inline edit (Telegram editMessageText) ----------------------
// Called by sendMessage when editingMessage is set. Validates the input
// (must be non-empty, must differ from the original), PATCHes via the
// Telegram action, and resets edit mode on success. On failure the banner
// stays so the agent can correct and retry.
const submitEdit = async () => {
  const next = sendingText.value.trim();
  const target = editingMessage.value;
  if (!target || isSending.value) return;
  if (!next) {
    childError.value = { message: 'Edit cannot be empty — use Delete to remove the message.' };
    return;
  }
  if (next === (target.content || '').trim()) {
    // No-op — just exit edit mode without hitting the API.
    cancelEdit();
    return;
  }
  isSending.value = true;
  try {
    const resp = await TelegramActionsAPI.editMessage(
      props.conversationId,
      target.id,
      next
    );
    if (resp.data && resp.data.ok === false) {
      childError.value = { message: `Edit failed: ${resp.data.error || 'unknown'}` };
      return;
    }
    // Vuex update so the bubble re-renders with the new content immediately.
    // Backend returns the refreshed message envelope — same pattern as the
    // react/un-react flow (see set_reaction_service.rb).
    if (resp.data?.message) {
      store.dispatch('updateMessage', resp.data.message);
    }
    editingMessage.value = null;
    editingDraftBackup.value = null;
    sendingText.value = '';
    clearCurrentDraft();
  } catch (e) {
    childError.value = { message: `Edit failed: ${e?.response?.data?.error || e?.message || e}` };
  } finally {
    isSending.value = false;
  }
};

const onKeyDown = e => {
  // Enter without Shift sends; Shift+Enter inserts newline
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
    return;
  }
  // Esc cancels edit mode — standard expectation for inline editors.
  // Don't fire when no edit is active so we don't steal focus from
  // unrelated Esc handlers (emoji picker, etc).
  if (e.key === 'Escape' && editingMessage.value) {
    e.preventDefault();
    cancelEdit();
  }
};

// ----- Header action buttons -------------------------------------------------
const showSearch  = ref(false);
const showInfo    = ref(false);
const showMore    = ref(false);
const searchQuery = ref('');

const toggleSearch = () => {
  showSearch.value = !showSearch.value;
  if (!showSearch.value) searchQuery.value = '';
  showInfo.value = false;
  showMore.value = false;
};
const toggleInfo = () => {
  showInfo.value = !showInfo.value;
  showSearch.value = false;
  showMore.value = false;
  if (showInfo.value) loadFangateLinkTitles();
};

// ===== Shared content (Telegram-style: Links / Media / Documents) ===========
// Derived from the loaded messages. Browser-independent: the links live in the
// DB messages, and their titles come from the vault's products (server state,
// the same for every browser) — nothing is stored only in this browser.
const shareTab = ref('links');                 // 'links' | 'media' | 'documents'
const FANGATE_LINK_RE = /https?:\/\/fangate\.(?:info|co)\/\S+/gi;
// link URL -> { title, description }, built from this inbox's vault products.
const fangateLinkTitles = ref({});
const fangateTitlesLoading = ref(false);

const loadFangateLinkTitles = async () => {
  if (!inboxId.value || !FangateAPI.isInboxConnected(inboxId.value)) return;
  fangateTitlesLoading.value = true;
  try {
    const resp = await FangateAPI.listProducts({ page: 1, limit: 100, inboxId: inboxId.value });
    const list = Array.isArray(resp) ? resp : (resp?.data ?? resp?.products ?? []);
    const map = {};
    for (const p of list) {
      const url = p.link || FangateAPI.buildProductLink(p.id);
      if (url) map[String(url).trim()] = {
        title: p.title || '',
        description: p.public_description || '',
        id: p.id ?? null,
        // Fangate stores prices as integer cents (e.g. 4400 = €44.00).
        price: typeof p.price === 'number' ? p.price : null,
      };
    }
    fangateLinkTitles.value = map;
  } catch (_) {
    // best-effort — links still show as URLs without titles
  } finally {
    fangateTitlesLoading.value = false;
  }
};

// Unique Fangate links found in the conversation, newest first, enriched with
// the product title/description where we can resolve it.
const sharedLinks = computed(() => {
  const out = [];
  const seen = new Set();
  for (const m of messages.value) {
    const text = String(m.content || '');
    const matches = text.match(FANGATE_LINK_RE);
    if (!matches) continue;
    for (const raw of matches) {
      const url = raw.replace(/[).,]+$/, '');   // strip trailing punctuation
      if (seen.has(url)) continue;
      seen.add(url);
      const meta = fangateLinkTitles.value[url] || {};
      out.push({
        url,
        title: meta.title || '',
        description: meta.description || '',
        id: meta.id ?? null,
        price: meta.price ?? null,
        at: m.created_at,
        messageId: m.id,   // jump-to-message target
      });
    }
  }
  return out.reverse();   // newest first
});

// Render a Fangate product price (integer cents) as EUR. The vault currency
// endpoint 403s for restricted tokens, so we hardcode EUR like FangateVaultView.
const fmtFangatePrice = cents => {
  if (typeof cents !== 'number') return '';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  } catch (_) {
    return `${(cents / 100).toFixed(2)} €`;
  }
};

const attachmentsByType = types => {
  const out = [];
  for (const m of messages.value) {
    for (const att of (m.attachments || [])) {
      if (types.includes(String(att.file_type || '').toLowerCase())) {
        out.push({ att, at: m.created_at, messageId: m.id });   // jump-to-message target
      }
    }
  }
  return out.reverse();
};
const sharedMedia     = computed(() => attachmentsByType(['image', 'video']));
const sharedDocuments = computed(() => attachmentsByType(['file', 'audio']));

// Compact two-line "on DD/MM/YYYY" + "at HH:MM" stamp for the Contact-info link
// list — when the link was shared (the source message's created_at, unix seconds).
const sharedWhen = ts => {
  const n = Number(ts);
  if (!n) return { date: '', time: '' };
  const d = new Date(n < 1e12 ? n * 1000 : n);
  const p = x => String(x).padStart(2, '0');
  return {
    date: `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`,
    time: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
};

// Click a shared-content item (link / media / document) → jump to its source
// message in the open conversation instead of opening it externally. Close the
// info overlay first so the highlighted bubble is visible underneath; goToMessage
// pages older if needed, then scrolls + flashes the row.
const jumpToShared = messageId => {
  if (!messageId) return;
  showInfo.value = false;
  goToMessage(messageId);
};

// Click on the contact name in the header → open the info panel AND force
// a fresh Telegram pull (bypasses the once-per-session sessionStorage gate).
// User-driven refresh: the agent explicitly clicked, so it's fine to spend
// one Telegram API hit even if the session has seen this contact already.
// Server-side 60s cooldown is still in effect to protect against spam.
const refreshingContact = ref(false);
const openContactInfo = () => {
  showInfo.value = true;
  showSearch.value = false;
  showMore.value = false;
  forceRefreshTelegramContact();
  loadFangateLinkTitles();
};
const forceRefreshTelegramContact = () => {
  const contactId = sender.value?.id;
  if (!contactId || refreshingContact.value) return;
  refreshingContact.value = true;
  // Reset the once-per-session marker so next chat-open also tries again.
  sessionStorage.removeItem(`fangate_tg_contact_refreshed_${contactId}`);
  sessionStorage.setItem(`fangate_tg_contact_refreshed_${contactId}`, String(Date.now()));
  ContactAPI.refreshTelegramProfile(contactId)
    .catch(() => { /* server-side cooldown swallowed — silent */ })
    .finally(() => { refreshingContact.value = false; });
};
const toggleMore = e => {
  e.stopPropagation();
  showMore.value = !showMore.value;
  showSearch.value = false;
  showInfo.value = false;
};
const closeMore = () => { showMore.value = false; };
const closePopovers = () => {
  showMore.value        = false;
  showEmojiPicker.value = false;
  showLabelPicker.value = false;   // close header label picker on outside click
  showLinkPopover.value = false;   // close the insert-link popover on outside click
};
onMounted(()  => document.addEventListener('click', closePopovers));
onUnmounted(() => document.removeEventListener('click', closePopovers));

// Filtered messages — search filters by message content (case-insensitive).
// Also hide messages deleted on Telegram (content_attributes.deleted, set by the
// delete action + mirror cleanup): they're gone on Telegram, so they should
// disappear here too. The update arrives via the ADD_MESSAGE subscription, which
// re-runs this computed → the bubble vanishes without a reload.
const displayedMessages = computed(() => {
  const visible = messages.value.filter(m => !m.content_attributes?.deleted);
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return visible;
  return visible.filter(m =>
    (m.content || '').toLowerCase().includes(q)
  );
});

// "More" menu actions ---------------------------------------------------------
const markUnread = async () => {
  closeMore();
  if (!props.conversationId) return;
  try {
    const { default: ConversationAPI } = await import('dashboard/api/inbox/conversation');
    await ConversationAPI.markMessagesUnread({ id: props.conversationId });
  } catch (e) {
    childError.value = { message: 'Failed to mark unread: ' + (e?.message ?? e) };
  }
};
const toggleMute = async () => {
  closeMore();
  const conv = currentChat.value;
  if (!conv) return;
  const wasMuted = !!conv.muted;
  // Optimistic flip: the backend mute! uses update_columns (no ActionCable
  // broadcast) and the controller returns head :ok, so the frontend never
  // learns the new state on its own. Flip conv.muted here so the header icon
  // updates this frame and the next toggle reads the right direction. The DB
  // already holds the truth (serializer ships `muted`), so a reload stays
  // correct; we revert this optimistic flip only if the API call fails.
  conv.muted = !wasMuted;
  try {
    const { default: ConversationAPI } = await import('dashboard/api/inbox/conversation');
    if (wasMuted) await ConversationAPI.unmute(conv.id);
    else          await ConversationAPI.mute(conv.id);
  } catch (e) {
    conv.muted = wasMuted; // revert on failure
    childError.value = { message: 'Failed to toggle mute: ' + (e?.message ?? e) };
  }
};
const resolveChat = async () => {
  closeMore();
  if (!props.conversationId) return;
  try {
    const { default: ConversationAPI } = await import('dashboard/api/inbox/conversation');
    await ConversationAPI.toggleStatus({
      conversationId: props.conversationId,
      status: 'resolved',
    });
  } catch (e) {
    childError.value = { message: 'Failed to resolve: ' + (e?.message ?? e) };
  }
};

// ===== Labels for the active chat ===========================================
// Labels come on the conversation object as an array of title strings.
const chatLabels = computed(() => currentChat.value?.labels ?? []);
const showLabelPicker = ref(false);
const labelPickerPos  = ref({ left: 0, top: 0 });

const openLabelPicker = e => {
  e.stopPropagation();
  const rect = e.currentTarget.getBoundingClientRect();
  labelPickerPos.value = { left: rect.left, top: rect.bottom + 4 };
  showLabelPicker.value = true;
};
const closeLabelPicker = () => { showLabelPicker.value = false; };

const toggleLabelOnChat = async lbl => {
  if (!props.conversationId) return;
  const current = new Set(chatLabels.value);
  if (current.has(lbl.title)) current.delete(lbl.title);
  else current.add(lbl.title);
  const next = [...current];
  // Optimistic update — patch the local conversation object
  if (currentChat.value) currentChat.value.labels = next;
  emit('labels-changed', { conversationId: props.conversationId, labels: next });
  try {
    // ConversationAPI from `dashboard/api/conversations` (NOT `inbox/conversation`)
    // exposes updateLabels(conversationId, labels: string[])
    const { default: ConvLabelsAPI } = await import('dashboard/api/conversations');
    await ConvLabelsAPI.updateLabels(props.conversationId, next);
  } catch (e) {
    childError.value = { message: 'Failed to update labels: ' + (e?.message ?? e) };
  }
};

// ===== Drafts ===============================================================
// Per-conversation message drafts: text the user typed but didn't send.
// Persisted to localStorage so the chat-list preview can render a "Draft:"
// prefix and so the input survives tab close. Keyed by conversation id.
const DRAFTS_KEY = 'fangate_drafts';
const readDrafts = () => {
  try { return JSON.parse(localStorage.getItem(DRAFTS_KEY) || '{}'); }
  catch (_) { return {}; }
};
const writeDrafts = d => {
  try {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(d));
    // Same-document write: the native `storage` event only fires in OTHER tabs,
    // so tell the chat list directly. Lets it refresh draft previews on change
    // instead of polling every second.
    window.dispatchEvent(new Event('fangate-drafts-changed'));
  } catch (_) {}
};
const saveDraft = (convId, text) => {
  if (!convId) return;
  const d = readDrafts();
  const t = String(text || '').trim();
  if (t) d[convId] = t; else delete d[convId];
  writeDrafts(d);
};

// When the user switches to a different conversation:
//   1. persist the current input as the draft of the OUTGOING conversation
//   2. load the INCOMING conversation's draft into the input (or clear)
// Uses a tracked-previous-id pattern so we know which conv to save against
// when the prop changes.
let prevConvIdForDrafts = props.conversationId;
watch(
  () => props.conversationId,
  newId => {
    // Save the text we had against the conv we're leaving.
    saveDraft(prevConvIdForDrafts, sendingText.value);
    prevConvIdForDrafts = newId;
    // Load the new conv's draft (or empty).
    if (!newId) { sendingText.value = ''; return; }
    const drafts = readDrafts();
    sendingText.value = drafts[newId] || '';
  },
  { immediate: false }
);

// When a message is successfully sent, the draft for THIS conv is gone.
// sendMessage clears sendingText.value already; we just need to drop the
// localStorage entry to avoid resurrecting it on next visit.
const clearCurrentDraft = () => saveDraft(props.conversationId, '');

// Persist the draft on every keystroke so the chat-list's "Draft" preview
// stays current without waiting for the user to switch chats. Skip the
// initial mount (no real edit yet) by gating on the prev id matching.
// Skip while editing a Telegram message — the textarea is hosting an
// existing message for inline edit, not a draft, so persisting it would
// overwrite the user's actual unsent draft.
watch(sendingText, txt => {
  if (editingMessage.value) return;
  if (props.conversationId && prevConvIdForDrafts === props.conversationId) {
    saveDraft(props.conversationId, txt);
  }
});

// ===== Show Notes ===========================================================
// Persisted per-conversation in conversation.custom_attributes.fangate_note
// (team-only — custom_attributes are never exposed to the contact/widget).
// Debounced save via the conversations store action, which echoes the merged
// attributes back into Vuex.
const showNotes = ref(false);
const noteText = ref('');

// --- Debounced save machinery (defined before the load watch so the watch's
// immediate run can call flushNote without hitting the TDZ) ---
let noteSaveTimer = null;
let pendingNote = null;            // { id, text } captured at edit time
const NOTE_SAVE_DELAY = 700;
const persistNote = (id, text) => {
  if (!id) return;
  const conv = (props.conversations || []).find(c => Number(c.id) === Number(id));
  // The backend REPLACES the whole custom_attributes jsonb, so merge into the
  // existing attributes to avoid wiping other keys.
  const merged = { ...(conv?.custom_attributes || {}), fangate_note: text };
  store.dispatch('updateCustomAttributes', { conversationId: id, customAttributes: merged });
};
const flushNote = () => {
  if (noteSaveTimer) { clearTimeout(noteSaveTimer); noteSaveTimer = null; }
  if (!pendingNote) return;
  const { id, text } = pendingNote;
  pendingNote = null;
  persistNote(id, text);
};
const saveNote = () => {
  pendingNote = { id: props.conversationId, text: noteText.value };
  if (noteSaveTimer) clearTimeout(noteSaveTimer);
  noteSaveTimer = setTimeout(flushNote, NOTE_SAVE_DELAY);
};

const toggleNotes = () => {
  if (showNotes.value) flushNote();   // closing → persist any pending edit now
  showNotes.value = !showNotes.value;
  emit('toggle-notes', showNotes.value);
};

// Load the note once per conversation — when currentChat first resolves for a
// new id. We deliberately DON'T reload on later custom_attributes updates so a
// websocket echo (or our own save) can't clobber what the agent is mid-typing.
let lastLoadedNoteConvId = null;
watch(
  currentChat,
  conv => {
    const id = conv?.id ?? null;
    if (id === lastLoadedNoteConvId) return;
    flushNote();                        // persist a pending edit for the old chat
    lastLoadedNoteConvId = id;
    noteText.value = conv?.custom_attributes?.fangate_note || '';
  },
  { immediate: true }
);

onUnmounted(() => flushNote());
</script>

<template>
  <!-- Empty state -->
  <div v-if="!currentChat" class="fca-empty">
    <div class="fca-empty-icon" aria-hidden="true">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="1.5"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    </div>
    <div class="fca-empty-label">No conversation selected</div>
    <div class="fca-empty-sub">Pick a chat from the list to start messaging.</div>
  </div>

  <!-- Selected chat -->
  <div v-else class="fangate-chat-area">

    <!-- ===== Fangate Live-Sale pop-up (dismissible) ===== -->
    <transition name="fca-sale">
      <div v-if="salePopup" class="fca-sale-pop" role="status">
        <span class="fca-sale-emoji">💰</span>
        <div class="fca-sale-body">
          <div class="fca-sale-title">Successful sale</div>
          <div class="fca-sale-sub">
            <span v-if="saleProduct" class="fca-sale-prod">{{ saleProduct }} · </span>
            <span class="fca-sale-amt">{{ saleMoney }}</span>
            <span v-if="salePopup.sale && salePopup.sale.buyer_email"> · {{ salePopup.sale.buyer_email }}</span>
          </div>
          <button v-if="salePopup.sale && salePopup.sale.buyer_email && !salePopup.sale.already_linked" class="fca-sale-assign" type="button" @click="assignSaleBuyer">
            Assign this buyer to a Chat
          </button>
        </div>
        <button class="fca-sale-x" type="button" aria-label="Dismiss" @click="dismissSale">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>
    </transition>

    <!-- ===== Custom Fangate header ===== -->
    <header class="fca-header">
      <button v-if="isMobile" class="fca-back" type="button" aria-label="Back to chats" @click="$emit('back')"
              style="display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:10px;border:none;background:transparent;color:var(--ink-strong);cursor:pointer;flex-shrink:0;margin:-2px 4px -2px -6px;">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
      </button>
      <div class="fca-avatar-wrap">
        <div class="fca-avatar">
          <img v-if="sender.thumbnail && !avatarBroken"
               :src="sender.thumbnail"
               :alt="sender.name"
               @error="avatarBroken = true" />
          <span v-else>{{ getInitials(sender.name) }}</span>
        </div>
        <span class="fca-online-dot"
              :class="{ 'fca-online-dot--off': !isOnline }"></span>
      </div>
      <div class="fca-meta">
        <!-- Name row: name + chat-type pill + label chips + "+ Label" -->
        <div class="fca-name-row">
          <button class="fca-name fca-name--clickable"
                  type="button"
                  :title="'Open ' + (sender.name || 'contact') + ' details'"
                  @click="openContactInfo">
            {{ sender.name || 'Unknown' }}
          </button>
          <!-- Group / Channel type chip — only rendered for group/supergroup/
               channel conversations so the agent always knows what kind of
               chat they're in (matches the listpane row indicator). -->
          <span v-if="currentChat?.additional_attributes?.chat_type === 'channel'"
                class="fca-ch-type fca-ch-type--channel">Channel</span>
          <span v-else-if="['group','supergroup'].includes(currentChat?.additional_attributes?.chat_type)"
                class="fca-ch-type fca-ch-type--group">Group</span>
          <span v-for="lbl in chatLabels" :key="lbl"
                class="fca-label-chip"
                @click.stop="toggleLabelOnChat({ title: lbl })">
            {{ lbl }} <span class="fca-label-x">×</span>
          </span>
          <button v-if="!isMobile" class="fca-label-add" @click.stop="openLabelPicker">+ Label</button>
        </div>
        <div class="fca-presence">{{ presenceText }}</div>
      </div>
      <div style="flex:1;"></div>
      <!-- Show Notes toggle -->
      <button class="fca-notes-btn" :class="{ 'is-active': showNotes }"
              @click="toggleNotes">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        <span v-if="!isMobile">{{ showNotes ? 'Hide Notes' : 'Show Notes' }}</span>
      </button>
      <button v-if="!isMobile" class="fca-action fca-action--search" :class="{ 'is-active': showSearch }"
              title="Search messages" @click="toggleSearch">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
        </svg>
      </button>
      <button class="fca-action" :class="{ 'is-active': showInfo }"
              title="Contact info" @click="toggleInfo">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      </button>
      <div v-if="!isMobile" style="position:relative;">
        <button class="fca-action" :class="{ 'is-active': showMore }"
                title="More actions" @click="toggleMore">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="1"/>
            <circle cx="12" cy="5"  r="1"/>
            <circle cx="12" cy="19" r="1"/>
          </svg>
        </button>
        <div v-if="showMore" class="fca-more-menu" @click.stop>
          <button class="ctx-item" @click="markUnread">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8"  x2="12"   y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Mark as unread
          </button>
          <button class="ctx-item" @click="toggleMute">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <template v-if="currentChat?.muted">
                <line x1="23" y1="9" x2="17" y2="15"/>
                <line x1="17" y1="9" x2="23" y2="15"/>
              </template>
              <template v-else>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
              </template>
            </svg>
            {{ currentChat?.muted ? 'Unmute' : 'Mute' }}
          </button>
          <div class="ctx-sep"></div>
          <button class="ctx-item" @click="resolveChat">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Mark as resolved
          </button>
        </div>
      </div>
      <!-- Fangate pill — mobile only. Opens the Fangate panel (Col 4) as a
           right slide-over so the agent can grab products/links without
           leaving the chat. Desktop keeps the always-on Col 4 panel. -->
      <button v-if="isMobile" class="fca-fg-pill" type="button"
              aria-label="Open Fangate panel" @click="$emit('open-fangate')">
        <svg class="fca-fg-wordmark" viewBox="0 5 315 44" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Fangate">
          <defs><linearGradient id="fg-grad-pill" x1="0%" y1="50%" x2="100%" y2="50%"><stop offset="0%" stop-color="#1a0a6b"/><stop offset="35%" stop-color="#3818b5"/><stop offset="70%" stop-color="#5b2ecf"/><stop offset="100%" stop-color="#7a3ce0"/></linearGradient></defs>
          <text x="0" y="48" font-family="Inter, system-ui, sans-serif" font-weight="900" font-size="56" letter-spacing="1.5" fill="url(#fg-grad-pill)">FANGATE.</text>
        </svg>
      </button>
    </header>

    <!-- Mobile-only search trigger — a lone magnifier floating at the top-left,
         just under the header (desktop keeps the trigger in the header cluster).
         It's a SIBLING of the persona overlay (not a child) because that overlay
         is pointer-events:none; tapping expands the shared search bar below and
         the trigger hides itself while the bar is open. -->
    <button v-if="isMobile && !showSearch" class="fca-msearch-trigger" type="button"
            title="Search messages" aria-label="Search messages" @click="toggleSearch">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
      </svg>
    </button>

    <!-- Persona pill — small centered chip that floats just under the chat
         header. Reminds the agent which account they're operating as so reply
         tone doesn't land in the wrong voice. Hidden on mobile while the search
         bar is open (they'd otherwise share the same strip). -->
    <div v-if="personaInboxName && !(isMobile && showSearch)" class="fca-persona-wrap">
      <div class="fca-persona-pill">
        You are chatting as
        <strong class="fca-persona-name">{{ personaInboxName }}</strong>
      </div>
    </div>

    <!-- ===== Label picker (position:fixed near + Label button) ===== -->
    <div v-if="showLabelPicker" class="fca-label-picker"
         :style="{ left: labelPickerPos.left + 'px', top: labelPickerPos.top + 'px' }"
         @click.stop>
      <div class="fca-label-picker-head">
        <span>Labels for this chat</span>
        <button class="fca-label-x-close" @click="closeLabelPicker">×</button>
      </div>
      <div v-if="!labels.length" class="fca-label-empty">
        No labels defined yet.<br>
        Go to <em>Settings → Labels</em> to create your first one.
      </div>
      <button v-for="lbl in labels" :key="lbl.id"
              class="fca-label-row"
              :class="{ active: chatLabels.includes(lbl.title) }"
              @click="toggleLabelOnChat(lbl)">
        <span class="fca-label-check" :class="{ checked: chatLabels.includes(lbl.title) }">
          <svg v-if="chatLabels.includes(lbl.title)" width="8" height="8" viewBox="0 0 12 12">
            <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5"
                      fill="none" stroke-linecap="round"/>
          </svg>
        </span>
        <span class="fca-label-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
        <span>{{ lbl.title }}</span>
      </button>
    </div>

    <!-- ===== Inline search bar (collapses out when showSearch=false) ===== -->
    <div v-if="showSearch" class="fca-searchbar">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <input
        v-model="searchQuery"
        class="fca-search-input"
        placeholder="Search in this conversation…"
        @keydown.escape="toggleSearch"
      />
      <button class="fca-search-close" @click="toggleSearch" title="Close">×</button>
    </div>

    <!-- ===== Notes panel (slide-in from right) ===== -->
    <div v-if="showNotes" class="fca-notes-panel">
      <div class="fca-notes-head">
        <h3>Notes</h3>
        <button class="fca-notes-close" @click="toggleNotes" title="Close notes">
          Close <span class="fca-notes-close-x">×</span>
        </button>
      </div>
      <div class="fca-notes-body">
        <textarea
          v-model="noteText"
          class="fca-notes-textarea"
          placeholder="Write internal notes about this chat — only visible to your team…"
          @input="saveNote"
        />
        <div class="fca-notes-hint">
          Saved automatically per conversation. Visible only to your team —
          never to the contact.
        </div>
      </div>
    </div>

    <!-- ===== Contact info side panel (overlay, slides in) ===== -->
    <div v-if="showInfo" class="fca-info-panel">
      <div class="fca-info-head">
        <h3>Contact info</h3>
        <div class="fca-info-head-actions">
          <button class="fca-info-refresh"
                  type="button"
                  :disabled="refreshingContact"
                  :title="refreshingContact ? 'Refreshing…' : 'Refresh from Telegram'"
                  @click="forceRefreshTelegramContact">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.2"
                 stroke-linecap="round" stroke-linejoin="round"
                 :class="{ 'fca-spin': refreshingContact }">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
          <button class="fca-notes-close" @click="toggleInfo" title="Close contact info">
            Close <span class="fca-notes-close-x">×</span>
          </button>
        </div>
      </div>
      <div class="fca-info-body">
        <div class="fca-info-avatar">
          <img v-if="sender.thumbnail && !avatarBroken" :src="sender.thumbnail" :alt="sender.name" @error="avatarBroken = true" />
          <span v-else>{{ getInitials(sender.name) }}</span>
        </div>
        <div class="fca-info-name">
          {{ sender.name || 'Unknown' }}
          <span v-if="senderAttrs.username" class="fca-info-username">(@{{ senderAttrs.username }})</span>
        </div>
        <div class="fca-info-presence">
          <span class="fca-info-dot" :class="{ off: !isOnline }"></span>
          {{ presenceText }}
        </div>

        <!-- ===== Shared content (Telegram-style) ===== -->
        <div class="fca-share">
          <div class="fca-share-tabs">
            <button class="fca-share-tab" :class="{ active: shareTab === 'links' }"
                    @click="shareTab = 'links'">Fangate-Links</button>
            <button class="fca-share-tab" :class="{ active: shareTab === 'media' }"
                    @click="shareTab = 'media'">Media</button>
            <button class="fca-share-tab" :class="{ active: shareTab === 'documents' }"
                    @click="shareTab = 'documents'">Documents</button>
          </div>

          <!-- Links (Fangate only) -->
          <div v-if="shareTab === 'links'">
            <div v-if="fangateTitlesLoading && !sharedLinks.length" class="fca-share-empty">Loading…</div>
            <div v-else-if="!sharedLinks.length" class="fca-share-empty">No Fangate links shared yet.</div>
            <a v-for="(l, i) in sharedLinks" :key="'lnk-' + i" class="fca-share-link"
               role="button" tabindex="0"
               @click="jumpToShared(l.messageId)"
               @keydown.enter.prevent="jumpToShared(l.messageId)">
              <span class="fca-share-link-main">
                <span class="fca-share-link-title">{{ l.title || l.url }}</span>
                <span v-if="l.id != null || l.price != null" class="fca-share-link-meta">
                  <span v-if="l.id != null" class="fca-share-link-id">Product #{{ l.id }}</span>
                  <span v-if="l.price != null" class="fca-share-link-price">{{ fmtFangatePrice(l.price) }}</span>
                </span>
                <span v-if="l.description" class="fca-share-link-desc">{{ l.description }}</span>
                <span class="fca-share-link-url">{{ l.url }}</span>
              </span>
              <span v-if="l.at" class="fca-share-link-when">on {{ sharedWhen(l.at).date }}<br>at {{ sharedWhen(l.at).time }}</span>
            </a>
          </div>

          <!-- Media -->
          <div v-else-if="shareTab === 'media'">
            <div v-if="!sharedMedia.length" class="fca-share-empty">No media shared yet.</div>
            <div v-else class="fca-share-grid">
              <a v-for="(m, i) in sharedMedia" :key="'med-' + i" class="fca-share-media"
                 role="button" tabindex="0"
                 @click="jumpToShared(m.messageId)"
                 @keydown.enter.prevent="jumpToShared(m.messageId)">
                <img v-if="m.att.thumb_url || m.att.data_url" :src="m.att.thumb_url || m.att.data_url" loading="lazy" alt="" />
                <span v-else class="fca-share-media-fallback">{{ m.att.file_type }}</span>
              </a>
            </div>
          </div>

          <!-- Documents -->
          <div v-else>
            <div v-if="!sharedDocuments.length" class="fca-share-empty">No documents shared yet.</div>
            <a v-for="(d, i) in sharedDocuments" :key="'doc-' + i" class="fca-share-doc"
               role="button" tabindex="0"
               @click="jumpToShared(d.messageId)"
               @keydown.enter.prevent="jumpToShared(d.messageId)">
              <i class="fca-share-doc-ico">📎</i>
              <span class="fca-share-doc-name">{{ (d.att.data_url || d.att.file_type || 'file').split('/').pop().split('?')[0] }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Error banner (if anything below crashed) ===== -->
    <div v-if="childError"
         style="margin:10px 14px;padding:10px 14px;border:1px solid #c00;
                background:#fee;color:#900;border-radius:8px;font-size:12px;
                font-family:monospace;">
      {{ childError.message }}
      <button @click="childError = null"
              style="margin-left:8px;padding:1px 6px;">dismiss</button>
    </div>

    <!-- ===== Message list (Chatwoot's <MessageList> from components-next) ===== -->
    <div ref="scrollerRef" class="fca-scroller" @scroll="onScroll">
      <!-- New-account safety reminder (#126): in the chat background while this
           MTProto account is < 7 days old, until dismissed. Reminder only. -->
      <div v-if="showNewAccountHint" class="fca-newacct-hint">
        <div class="fca-newacct-hint-body">
          <strong>New account</strong>
          <span>Keep things natural — avoid identical bulk messages and rapid-fire sending. It lowers the risk of Telegram flagging this account.</span>
        </div>
        <button
          type="button"
          class="fca-newacct-hint-x"
          aria-label="Dismiss reminder"
          @click="dismissNewAccountHint"
        >×</button>
      </div>
      <div v-if="isLoading" class="fca-loading">Loading messages…</div>
      <div v-else-if="messages.length === 0" class="fca-loading">No messages yet</div>
      <template v-else-if="currentUserId">
        <!-- Top-of-list pagination indicator -->
        <div v-if="isLoadingOlder" class="fca-loading-older">
          <span class="fca-spinner fca-spinner--dark" aria-hidden="true"></span>
          Loading older messages…
        </div>
        <div v-else-if="allLoaded" class="fca-history-end">
          — start of conversation —
        </div>
        <MessageList
          :messages="displayedMessages"
          :current-user-id="currentUserId"
          :is-an-email-channel="false"
          :is-telegram-channel="true"
          :inbox-supports-reply-to="{ incoming: true, outgoing: true }"
        />
      </template>
    </div>

    <!-- ===== Reply box (prototype-parity compose toolbar) ===== -->
    <footer class="fca-reply-wrap" @click.stop>

      <!-- Reply-to banner (Telegram-style "replying to") -->
      <div v-if="replyToMessage && !editingMessage" class="fca-reply-banner">
        <div class="fca-reply-banner-bar"></div>
        <div class="fca-reply-banner-body">
          <div class="fca-reply-banner-label">Replying to</div>
          <div class="fca-reply-banner-text">{{ replyToMessage.content || '(attachment)' }}</div>
        </div>
        <button class="fca-reply-banner-x" title="Cancel reply" @click="cancelReply">×</button>
      </div>

      <!-- Edit banner (#173) — same layout as reply, amber accent so the
           agent immediately registers they're editing an existing message,
           not composing a new one. Pencil-tick icon hammers it home. -->
      <div v-if="editingMessage" class="fca-reply-banner fca-reply-banner--edit">
        <div class="fca-reply-banner-bar"></div>
        <div class="fca-reply-banner-body">
          <div class="fca-reply-banner-label">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.2"
                 stroke-linecap="round" stroke-linejoin="round"
                 style="vertical-align: -1px; margin-right: 4px;">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Editing message
          </div>
          <div class="fca-reply-banner-text">{{ editingMessage.content || '(empty)' }}</div>
        </div>
        <button class="fca-reply-banner-x" title="Cancel edit" @click="cancelEdit">×</button>
      </div>

      <!-- Attachment chip row -->
      <div v-if="pendingFiles.length" class="fca-attachments">
        <div v-for="(file, idx) in pendingFiles" :key="idx"
             class="fca-attach-chip"
             :class="{ 'fca-attach-chip--voice': file._isVoice }">
          <!-- Voice icon if marked, else generic paperclip -->
          <svg v-if="file._isVoice" width="11" height="11" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8"  y1="23" x2="16" y2="23"/>
          </svg>
          <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
          <span v-if="file._isVoice" class="fca-attach-voice-tag" title="Will send as a Telegram voice memo (round mic bubble)">Voice</span>
          <span class="fca-attach-name">{{ file.name }}</span>
          <span class="fca-attach-size">{{ Math.round(file.size / 1024) }} KB</span>
          <button class="fca-attach-rm" @click.stop="removeFile(idx)" title="Remove">×</button>
        </div>
      </div>

      <!-- AI Suggestions row (3 variants when generated) -->
      <div v-if="aiSuggestions.length" class="fca-ai-suggestions">
        <div class="fca-ai-head">
          <span>✨ ChatGPT suggestions</span>
          <button class="fca-ai-dismiss" @click="dismissSuggestions">×</button>
        </div>
        <div v-for="s in aiSuggestions" :key="s.tone"
             class="fca-ai-card" @click="useAiSuggestion(s)">
          <span class="fca-ai-tone">{{ s.tone }}</span>
          <span class="fca-ai-text">{{ s.text }}</span>
        </div>
      </div>

      <!-- Toolbar row (above input) — Add file / Emoji-GIF / Voice -->
      <div class="fca-toolbar-row">
        <input ref="fileInputRef" type="file" multiple
               style="display:none" @change="onFilePick" />
        <!-- Telegram's /sendVoice endpoint accepts only OGG/Opus, MP3, and
             M4A natively. WhatsApp voice exports (.ogg Opus) and iPhone /
             Android voice memos (M4A or MP3) all match. Anything else
             (WebM browser recordings, FLAC, WAV, raw AAC) gets rejected
             with a clear error. We restrict the picker upfront via accept
             so the user can't pick a doomed file in the first place.
             Both explicit MIME types AND file extensions are listed —
             some OSes / mobile browsers don't populate the mime type. -->
        <input ref="voiceInputRef" type="file"
               accept="audio/ogg,audio/opus,audio/mpeg,audio/mp4,audio/x-m4a,.ogg,.opus,.mp3,.m4a"
               style="display:none" @change="onVoiceFile" />

        <button class="fca-tool-btn" title="Attach file" @click="pickFiles">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
          Add file
        </button>

        <div style="position:relative;">
          <button class="fca-tool-btn" :class="{ 'is-active': showEmojiPicker }"
                  title="Emoji / GIF" @click.stop="toggleEmojiPicker">
            Add
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
              <line x1="9"  y1="9" x2="9.01"  y2="9"/>
              <line x1="15" y1="9" x2="15.01" y2="9"/>
            </svg>
            / GIF
          </button>

          <!-- Emoji + GIF picker -->
          <div v-if="showEmojiPicker" class="fca-emoji-picker" @click.stop>
            <div class="fca-emoji-tabs">
              <button class="fca-emoji-tab" :class="{ active: emojiTab === 'emoji' }"
                      @click="emojiTab = 'emoji'">Emote</button>
              <button class="fca-emoji-tab" :class="{ active: emojiTab === 'gif' }"
                      @click="emojiTab = 'gif'">GIF</button>
            </div>
            <div v-if="emojiTab === 'emoji'" class="fca-emoji-grid">
              <button v-for="emo in EMOJI_LIST" :key="emo"
                      class="fca-emoji-btn"
                      @click="insertEmoji(emo)">{{ emo }}</button>
            </div>
            <div v-else class="fca-gif-pane">
              <input v-model="gifQuery" class="fca-gif-search"
                     placeholder="Search GIFs…" @input="onGifSearch" />
              <div v-if="gifLoading" class="fca-gif-empty">Loading…</div>
              <div v-else-if="gifError" class="fca-gif-empty">{{ gifError }}</div>
              <div v-else-if="!gifResults.length" class="fca-gif-empty">No GIFs found.</div>
              <div v-else class="fca-gif-grid">
                <button v-for="g in gifResults" :key="g.id" type="button"
                        class="fca-gif-item" :title="g.title || 'GIF'"
                        @click="sendGif(g)">
                  <img :src="g.preview" :alt="g.title || 'GIF'" loading="lazy" />
                </button>
              </div>
              <div class="fca-gif-attrib">Powered by GIPHY</div>
            </div>
          </div>
        </div>

        <button class="fca-tool-btn"
                title="Send as voice memo (round mic bubble). Accepts OGG/Opus, MP3, M4A — Telegram only natively supports those three."
                @click="triggerVoice">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8"  y1="23" x2="16" y2="23"/>
          </svg>
          Voice
        </button>

        <div class="fca-url-wrap">
          <button class="fca-tool-btn" :class="{ 'is-active': showLinkPopover }"
                  title="Insert a link — custom text + target URL"
                  @click.stop="toggleLinkPopover">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            URL
          </button>

          <!-- Insert-link popover: visible text + target URL → [text](url),
               which renders as a blue underlined link in-chat AND a real
               clickable link for the Telegram recipient (see insertLink). -->
          <div v-if="showLinkPopover" class="fca-link-pop" @click.stop>
            <label class="fca-link-label">Link text</label>
            <input v-model="linkText" class="fca-link-input"
                   placeholder="Shown in the chat (blue, underlined)"
                   @keydown.enter.prevent="insertLink" />
            <label class="fca-link-label">URL</label>
            <input v-model="linkUrl" class="fca-link-input"
                   placeholder="https://example.com"
                   @keydown.enter.prevent="insertLink" />
            <div class="fca-link-actions">
              <button type="button" class="fca-link-cancel"
                      @click="showLinkPopover = false">Cancel</button>
              <button type="button" class="fca-link-insert"
                      :disabled="!linkUrl.trim()" @click="insertLink">Insert link</button>
            </div>
          </div>
        </div>

        <div style="flex:1;"></div>
      </div>

      <!-- Input row: textarea + send -->
      <div class="fca-input-row">
        <!-- Compose wrap: textarea on top, tools-footer (AI suggest + counter) below
             — both inside the same border so they can't escape the input area -->
        <div class="fca-compose-wrap">
          <textarea
            ref="composerTextareaRef"
            v-model="sendingText"
            class="fca-reply-input"
            rows="3"
            :maxlength="MAX_CHARS"
            :placeholder="editingMessage ? 'Edit message… (Enter to save, Esc to cancel)' : 'Type a message…  (Shift+Enter for newline)'"
            :disabled="isSending"
            @keydown="onKeyDown"
          />
          <div class="fca-compose-footer">
            <button v-if="showAiSuggest" class="fca-ai-btn"
                    :title="isGeneratingAI ? 'Generating…' : 'Generate reply suggestions with ChatGPT'"
                    :disabled="isGeneratingAI"
                    @click="generateAiReply">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494z"/>
              </svg>
              {{ isGeneratingAI ? 'Thinking…' : 'Suggest reply' }}
            </button>
            <div style="flex:1;"></div>
            <span class="fca-char-counter">{{ charCount }} / {{ MAX_CHARS }}</span>
          </div>
        </div>

        <div class="fca-send-stack">
          <button
            class="fca-send-btn"
            :class="{ 'fca-send-btn--edit': editingMessage }"
            :disabled="(!sendingText.trim() && !pendingFiles.length) || isSending"
            @click="sendMessage"
            :title="editingMessage ? (isSending ? 'Saving…' : 'Save edit (Enter)') : (isSending ? 'Sending…' : 'Send')"
          >
            <!-- Edit-mode: checkmark icon instead of paper-plane.
                 Same button class so layout stays identical. -->
            <svg v-if="editingMessage && !isSending" width="16" height="16"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <svg v-else-if="!isSending" width="16" height="16" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 2 11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
            <span v-else class="fca-spinner" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
// ============================================================================
// Layout
// ============================================================================
.fangate-chat-area {
  position: relative;          /* anchor for the contact-info side panel */
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--bg-main);
  // Chat wallpaper lives on the ROOT now (was only on the message surface) so the
  // faint hairlines show CONTINUOUSLY behind the floating header card's margin —
  // the header reads as truly floating over the chat, its top/side/corner gaps
  // revealing the chat backdrop. Scroller + message surface are transparent so
  // this single backdrop shows through everywhere.
  background-image: repeating-linear-gradient(45deg, rgba(74, 53, 32, 0.05) 0, rgba(74, 53, 32, 0.05) 1px, transparent 1px, transparent 13px);
}

/* Fangate Live-Sale pop-up — dismissible nudge, floats top-right under the header. */
.fca-sale-pop {
  position: absolute;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 11px;
  max-width: calc(100% - 32px);
  padding: 11px 12px 11px 14px;
  /* Fangate brand gradient — deep indigo → lighter magenta-purple (matches the logo). */
  background: linear-gradient(120deg, #5b2ecf 0%, #9b2fc4 100%);
  color: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(91, 46, 207, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.20);
}
.fca-sale-emoji { font-size: 20px; line-height: 1; flex-shrink: 0; }
.fca-sale-body { min-width: 0; }
.fca-sale-title { font: 700 12.5px 'Inter', sans-serif; letter-spacing: 0.01em; }
.fca-sale-sub { font: 500 12px 'Inter', sans-serif; opacity: 0.95; margin-top: 1px; white-space: normal; word-break: break-word; }
.fca-sale-prod { font-weight: 600; }
.fca-sale-assign {
  display: inline-flex; align-items: center; margin-top: 7px;
  border: 1px solid rgba(255, 255, 255, 0.45); background: rgba(255, 255, 255, 0.14);
  color: #fff; border-radius: 999px; padding: 3px 11px;
  font: 600 11px 'Inter', sans-serif; cursor: pointer; letter-spacing: 0.01em;
}
.fca-sale-assign:hover { background: rgba(255, 255, 255, 0.26); border-color: rgba(255, 255, 255, 0.7); }
.fca-sale-amt { font-variant-numeric: tabular-nums; }
.fca-sale-x {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
  transition: background 0.14s;
}
.fca-sale-x:hover { background: rgba(255, 255, 255, 0.3); }
.fca-sale-enter-active, .fca-sale-leave-active { transition: opacity 0.2s; }
.fca-sale-enter-from, .fca-sale-leave-to { opacity: 0; }
@media (max-width: 768px) {
  .fca-sale-pop { top: 60px; right: 10px; left: 10px; transform: none; max-width: none; }
}

// ============================================================================
// Header
// ============================================================================
.fca-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  // Floating rounded header card — soft shadow instead of a hard divider line.
  margin: 6px 8px;
  border-radius: 14px;
  background: var(--bg-panel);
  box-shadow: 0 1px 2px rgba(20, 22, 40, 0.05), 0 3px 9px rgba(20, 22, 40, 0.04);
  flex-shrink: 0;
  user-select: none;
}
// New-account safety reminder (#126) — gentle warm-cream card centered at the
// top of the chat background. Reminder only, dismissible.
.fca-newacct-hint {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px auto;
  max-width: 520px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fef6ec;
  border: 1px solid #f0d8b8;
  color: #7a5a2e;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  line-height: 1.45;
}
.fca-newacct-hint-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fca-newacct-hint-body strong {
  color: #b5701a;
  font-weight: 700;
}
.fca-newacct-hint-x {
  margin-left: auto;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #b5701a;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
  opacity: 0.6;
}
.fca-newacct-hint-x:hover {
  opacity: 1;
}
// Persona pill — small centered chip that floats just under the chat
// header. Reminds the agent whose persona they're speaking as without
// taking a full row. The wrap centers the pill; the pill itself is
// inline-flex so it sizes to its content.
.fca-persona-wrap {
  // OVERLAY (absolute) so the pill genuinely FLOATS over the message area
  // instead of sitting in its own full-width strip. The old in-flow strip read
  // as a distinct "background row" because the message surface renders a
  // slightly different shade than var(--bg-main), so the strip never blended.
  // Anchored to .fangate-chat-area (position: relative). pointer-events:none so
  // it never blocks clicks on the messages underneath.
  position: absolute;
  top: 80px;            // clear BELOW the floating header card + its margin (no overlap)
  left: 0;
  right: 0;
  z-index: 6;
  display: flex;
  justify-content: center;
  padding: 6px 12px 2px;
  background: transparent;
  user-select: none;
  pointer-events: none;
}
.fca-persona-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 12px;
  border-radius: 999px;
  // OPAQUE Telegram-cyan tinted surface + matching border so the pill reads as
  // "channel identity" at a glance, distinct from the corporate-teal of the chat
  // list. Solid (not translucent) so message content behind the floating pill
  // never bleeds through it.
  background: #eaf6fc;
  border: 1px solid rgba(34, 158, 217, 0.35);
  // Telegram-tinted drop-shadow so the pill reads as "floating" below the header
  // rather than glued onto the surface.
  box-shadow: 0 2px 8px rgba(34, 158, 217, 0.18);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #0d6b9c;
  letter-spacing: 0.01em;
  pointer-events: auto;
}
.fca-persona-name {
  // Telegram brand blue — slightly darker than the badge tint so the
  // persona name pops against the cyan-washed pill without losing
  // readability.
  color: #0088CC;
  font-weight: 800;
}
.fca-avatar-wrap { position: relative; flex-shrink: 0; }
.fca-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: var(--bg-subtle); border: 1.5px solid var(--border-soft);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Libre Baskerville', serif; font-size: 14px; font-weight: 700;
  line-height: 1;
  color: var(--accent-strong); overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fca-online-dot {
  position: absolute; bottom: 0; right: 0;
  width: 10px; height: 10px; border-radius: 50%;
  background: #10b981; border: 2px solid var(--bg-panel);
  pointer-events: none;
  &--off { background: #cbd5d0; }
}
.fca-meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.fca-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.fca-name {
  font-family: 'Libre Baskerville', serif; font-size: 14px; font-weight: 700;
  color: var(--ink-strong); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fca-name--clickable {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
  border-bottom: 1px dashed transparent;
  transition: color 0.12s, border-color 0.12s;
  &:hover { color: var(--accent-strong); border-bottom-color: var(--accent); }
}
.fca-info-head-actions {
  display: flex; align-items: center; gap: 6px;
}
.fca-info-refresh {
  width: 34px; height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(91, 46, 207, 0.18);
  background: #fff;
  color: var(--accent-strong, #5b2ecf);
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover:not(:disabled) { background: var(--accent-strong, #5b2ecf); color: #fff; border-color: var(--accent-strong, #5b2ecf); }
  &:disabled { opacity: 0.55; cursor: progress; }
}
@keyframes fca-rot {
  to { transform: rotate(360deg); }
}
.fca-spin {
  animation: fca-rot 0.9s linear infinite;
  transform-origin: 50% 50%;
}
.fca-label-chip {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 600;
  border: 1px solid var(--accent-ring);
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: var(--accent); color: #fff; }
}
// Group / Channel type indicator beside the contact name in the chat header
// — same palette as the listpane row chip (.lp-ch-type) so the two views
// reinforce each other.
.fca-ch-type {
  display: inline-flex; align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
}
.fca-ch-type--group {
  background: rgba(13, 148, 136, 0.14);
  color: #0d9488;
}
.fca-ch-type--channel {
  background: rgba(91, 33, 182, 0.12);
  color: #5b21b6;
}
.fca-label-x {
  opacity: 0.7;
  font-size: 12px;
  line-height: 1;
}
.fca-label-add {
  display: inline-flex; align-items: center; gap: 2px;
  padding: 1px 7px !important;
  border-radius: 4px;
  border: 1px dashed var(--border-medium);
  background: transparent;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
  &:hover {
    border-color: var(--accent);
    color: var(--accent-strong);
  }
}

// Show Notes button (header right area)
.fca-notes-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 5px;
  padding: 5px 10px !important;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  color: var(--ink-muted);
  cursor: pointer;
  white-space: nowrap;   /* never wrap "Show Notes" to 2 lines → keeps the header
                            one row tall so the persona pill (top:72px) clears it */
  flex-shrink: 0;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.12s;
  &:hover, &.is-active {
    background: var(--accent-tint);
    color: var(--accent-strong);
    border-color: var(--accent-ring);
  }
}

// Label picker (position:fixed, anchored to + Label button)
.fca-label-picker {
  position: fixed;
  z-index: 9999;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(11, 20, 20, 0.18);
  padding: 6px;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
}
.fca-label-picker-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px 8px;
  font-family: 'Inter', sans-serif;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-muted);
}
.fca-label-x-close {
  border: none; background: transparent;
  font-size: 14px; padding: 0 4px !important;
  color: var(--ink-muted); cursor: pointer;
  &:hover { color: var(--ink-strong); }
}
.fca-label-empty {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  padding: 8px 10px;
  font-style: italic;
  line-height: 1.5;
  em { font-style: normal; font-weight: 600; color: var(--accent-strong); }
}
.fca-label-row {
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  &:hover { background: var(--bg-subtle); }
  &.active { color: var(--accent-strong); background: var(--accent-tint); }
}
.fca-label-check {
  width: 13px; height: 13px; border-radius: 3px;
  flex-shrink: 0;
  border: 1.5px solid var(--border-medium);
  background: transparent;
  display: flex; align-items: center; justify-content: center;
  &.checked { border-color: var(--accent); background: var(--accent); }
}
.fca-label-dot {
  width: 8px; height: 8px; border-radius: 50%;
  flex-shrink: 0;
}

// Notes side panel
.fca-notes-panel {
  position: absolute;
  top: 0; right: 0;            // no `bottom` → height fits the content, so the
                              // chat area shows through below the notes card
  width: 340px;
  max-height: 100%;
  background: var(--bg-panel);
  border-left: 1px solid var(--border-soft);
  border-bottom: 1px solid var(--border-soft);
  border-bottom-left-radius: 12px;
  box-shadow: -8px 8px 24px rgba(11, 20, 20, 0.12);
  display: flex; flex-direction: column;
  z-index: 450;
}
.fca-notes-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-soft);
  h3 {
    font-family: 'Libre Baskerville', serif;
    font-size: 14px; font-weight: 700;
    color: var(--ink-strong);
  }
}
// "Close ✕" — the whole control is clickable and closes the panel.
.fca-notes-close {
  display: inline-flex; align-items: center; gap: 5px;
  border: none; background: transparent; cursor: pointer;
  font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600;
  color: var(--ink-muted);
  padding: 4px 6px; border-radius: 7px;
  &:hover { color: var(--ink-strong); background: var(--bg-subtle); }
  .fca-notes-close-x { font-size: 16px; line-height: 1; }
}
.fca-notes-body {
  padding: 14px 18px;
  flex: 1;
  display: flex; flex-direction: column;
  gap: 10px;
}
.fca-notes-textarea {
  flex: none;
  height: 280px;          // fixed, compact — not the full panel height
  min-height: 120px;
  resize: vertical;       // let the user grow it if they need more room
  padding: 10px 12px;
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  background: var(--bg-main);
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  &::placeholder { color: var(--ink-muted); }
  &:focus {
    border-color: var(--accent-ring);
    box-shadow: 0 0 0 2px var(--accent-tint);
  }
}
.fca-notes-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  line-height: 1.45;
}
.fca-presence {
  font-family: 'Inter', sans-serif; font-size: 11px; color: var(--ink-muted);
}
.fca-action {
  display: inline-flex !important;
  align-items: center; justify-content: center;
  width: 30px; height: 30px; padding: 0 !important;
  border-radius: 8px; border: 1px solid transparent;
  background: transparent; color: var(--ink-muted);
  cursor: pointer; transition: all 0.12s;
  &:hover, &.is-active {
    color: var(--accent-strong);
    background: var(--accent-tint);
    border-color: var(--accent-ring);
  }
}

// "More" dropdown
.fca-more-menu {
  position: absolute;
  top: 38px; right: 0;
  min-width: 200px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(11, 20, 20, 0.14);
  padding: 5px;
  z-index: 200;
}

// Inline search bar
.fca-searchbar {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 18px;
  border-bottom: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-muted);
}
.fca-search-input {
  flex: 1;
  border: none; background: transparent; outline: none;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--ink-strong);
  &::placeholder { color: var(--ink-muted); }
}
.fca-search-close {
  border: none; background: transparent;
  width: 22px; height: 22px;
  border-radius: 5px;
  padding: 0 !important;
  font-size: 16px;
  color: var(--ink-muted);
  cursor: pointer;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}

// Contact info side panel (overlay above chat content)
.fca-info-panel {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 320px;
  background: var(--bg-panel);
  border-left: 1px solid var(--border-soft);
  box-shadow: -8px 0 24px rgba(11, 20, 20, 0.10);
  display: flex; flex-direction: column;
  z-index: 500;
}
.fca-info-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-soft);
  h3 {
    font-family: 'Libre Baskerville', serif;
    font-size: 14px; font-weight: 700;
    color: var(--ink-strong);
  }
}
.fca-info-body {
  padding: 20px 18px;
  overflow-y: auto;
  flex: 1;
  text-align: center;
}
.fca-info-avatar {
  width: 80px; height: 80px;
  border-radius: 50%;
  margin: 0 auto 10px;
  background: var(--accent-tint);
  border: 2px solid var(--accent-ring);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Libre Baskerville', serif;
  font-size: 28px; font-weight: 700;
  color: var(--accent-strong);
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fca-info-avatar-hint {
  margin: -4px auto 10px;
  max-width: 240px;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; line-height: 1.4;
  color: #8c7ad6;
  text-align: center;
  background: rgba(91, 46, 207, 0.05);
  border: 1px dashed rgba(91, 46, 207, 0.2);
  border-radius: 8px;
  padding: 6px 10px;
}
.fca-info-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 15px; font-weight: 700;
  color: var(--ink-strong);
}
.fca-info-username {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 400;
  color: var(--ink-muted);
  margin-left: 4px;
}
.fca-info-presence {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
}
.fca-info-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #10b981;
  &.off { background: #cbd5d0; }
}
.fca-info-list { margin-top: 20px; text-align: left; }
.fca-info-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-soft);
  &:last-child { border-bottom: none; }
  dt {
    font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 600;
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  dd {
    font-family: 'Inter', sans-serif;
    font-size: 12.5px;
    color: var(--ink-strong);
    margin: 0;
    text-align: right;
    word-break: break-all;
  }
}

// ===== Shared content (Links / Media / Documents) =====
.fca-share { margin-top: 22px; text-align: left; }
.fca-share-tabs {
  display: flex; gap: 4px; margin-bottom: 12px;
  border-bottom: 1px solid var(--border-soft);
}
.fca-share-tab {
  flex: 1; border: none; background: transparent; cursor: pointer;
  font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600;
  color: var(--ink-muted); padding: 7px 4px;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  &:hover { color: var(--ink-strong); }
  &.active { color: #047857; border-bottom-color: #047857; }
}
.fca-share-empty {
  font-family: 'Inter', sans-serif; font-size: 12px; font-style: italic;
  color: var(--ink-muted); padding: 10px 0;
}
.fca-share-link {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 8px 0; border-bottom: 1px solid var(--border-soft);
  text-decoration: none; cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover .fca-share-link-title { color: #047857; }
}
.fca-share-link-main {
  flex: 1 1 auto; min-width: 0;
  display: flex; flex-direction: column; gap: 1px;
}
.fca-share-link-when {
  flex: 0 0 auto; text-align: right; padding-top: 1px;
  font-family: 'Inter', sans-serif; font-size: 9.5px; line-height: 1.35;
  color: var(--ink-muted); white-space: nowrap;
}
.fca-share-link-title {
  font-family: 'Inter', sans-serif; font-size: 12.5px; font-weight: 600;
  color: var(--ink-strong); word-break: break-word;
}
.fca-share-link-meta {
  display: flex; align-items: center; gap: 8px; margin: 1px 0;
  font-family: 'Inter', sans-serif; font-size: 11px;
}
.fca-share-link-id { color: var(--ink-muted); }
.fca-share-link-price { color: #047857; font-weight: 700; }
.fca-share-link-desc {
  font-family: 'Inter', sans-serif; font-size: 11px; color: var(--ink-medium);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fca-share-link-url {
  font-family: 'Inter', sans-serif; font-size: 10.5px; color: #047857;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fca-share-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
}
.fca-share-media {
  aspect-ratio: 1; border-radius: 8px; overflow: hidden; cursor: pointer;
  background: var(--bg-subtle); border: 1px solid var(--border-soft);
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fca-share-media-fallback { font-size: 10px; color: var(--ink-muted); }
.fca-share-doc {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid var(--border-soft);
  text-decoration: none; color: var(--ink-strong); cursor: pointer;
  font-family: 'Inter', sans-serif; font-size: 12px;
  &:last-child { border-bottom: none; }
  &:hover { color: #047857; }
}
.fca-share-doc-ico { font-style: normal; }
.fca-share-doc-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

// ============================================================================
// Empty state
// ============================================================================
.fca-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px; height: 100%;
  background: var(--bg-main); color: var(--ink-muted); text-align: center;
}
.fca-empty-icon { color: var(--ink-muted); opacity: 0.45; }
.fca-empty-label {
  font-family: 'Libre Baskerville', serif; font-size: 14px; font-weight: 700;
  color: var(--ink-medium); margin-top: 4px;
}
.fca-empty-sub {
  font-family: 'Inter', sans-serif; font-size: 12px; color: var(--ink-muted);
}

// ============================================================================
// Scroller + message list
// ============================================================================
.fca-scroller {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: transparent;

  // ---- Per-bubble action trigger (Chatwoot's MessageContextMenu) -------
  // Default Chatwoot UX: kebab icon hidden until parent hover. We want it
  // always visible so agents see the action affordance without hovering.
  //
  // Tailwind JIT may escape the `group-hover/context-menu:` class name in
  // ways that break attribute-based selectors. Safer: target any button
  // with the `invisible` utility inside the scroller — within message-list
  // scope the only invisible buttons are these kebab triggers.
  :deep(button.invisible) {
    visibility: visible !important;
    opacity: 0.55 !important;
    transition: opacity 0.15s, transform 0.1s;
    &:hover { opacity: 1 !important; transform: translateY(1px); }
  }

  // ---- "read" double-check tick: warm gold so it pops on the teal bubble ----
  // MessageStatus.vue renders the SAME double-check icon for delivered and read;
  // only the colour class differs (read uses the arbitrary `text-[#7EB6FF]`,
  // which the prod UnoCSS build doesn't emit). On our teal outgoing bubble the
  // delivered tick is white and a blue read tick was barely visible, so we match
  // the read class string directly and paint it gold (#fbbf24) — high contrast
  // on teal, clearly distinct from the white delivered tick. The tick is a
  // UnoCSS mask <span> (colour = currentColor/background-color); set colour +
  // background-color, plus svg fill/stroke as a belt-and-braces fallback.
  :deep([class*="7EB6FF"]) {
    color: #fbbf24 !important;
    background-color: #fbbf24 !important;
    svg { color: #fbbf24 !important; fill: #fbbf24 !important; stroke: #fbbf24 !important; }
  }

  // ---- Hide the per-message sender avatar (agent initials) --------------
  // Message.vue renders a 24px sender Avatar in the [grid-area:avatar] column
  // on outgoing bubbles (it already omits it on grouped runs, so the grid
  // collapses cleanly when it's absent). Creator-Chat doesn't want a "who
  // sent it" initials badge on every bubble — hide that column's content.
  :deep([class*="grid-area:avatar"]) { display: none !important; }
}
.fca-loading {
  padding: 32px 16px;
  text-align: center;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}
.fca-loading-older {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
}
.fca-history-end {
  text-align: center;
  padding: 10px 16px 4px;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-style: italic;
  opacity: 0.7;
}

// Theme overrides for the embedded <MessageList>
// (uses Tailwind n-* utility classes from Chatwoot's theme)
:deep(.bg-n-surface-1) {
  // Transparent — the wallpaper now lives on .fangate-chat-area (the root) as one
  // continuous backdrop behind the floating header + the messages.
  background: transparent !important;
}

:deep(.right-bubble) {
  background: var(--accent) !important;
  color: #fff !important;
  // Increment 5: softer Telegram-style radii (tail corner at bottom-right stays
  // sharp) + a tinted soft shadow so outgoing bubbles read as raised.
  border-radius: 18px 18px 5px 18px !important;
  box-shadow: 0 1px 2px rgba(13, 80, 74, 0.20), 0 4px 14px rgba(13, 80, 74, 0.14) !important;
  // Nested rules come AFTER declarations (Dart Sass mixed-decls).
  * { color: #fff !important; }
  // Links keep the white text (on teal) but get underlined so they read as links.
  .prose a, .prose a * { text-decoration: underline !important; }
}
:deep(.left-bubble) {
  background: #fff !important;
  color: var(--ink-strong) !important;
  // Borderless + soft shadow (modern; replaces the old hairline border), tail
  // at bottom-left to mirror the outgoing bubble.
  border: none !important;
  border-radius: 18px 18px 18px 5px !important;
  box-shadow: 0 1px 2px rgba(20, 22, 40, 0.07), 0 4px 14px rgba(20, 22, 40, 0.06) !important;
  // Nested rules AFTER declarations (Dart Sass mixed-decls). Force the message
  // text (.prose) + reply-quote dark, matching the fixed white bubble — inner
  // prose carries theme tokens (text-n-slate-12) that flip to WHITE under OS dark
  // mode → unreadable on white (the mobile bug). The per-sender name label is NOT
  // .prose, so it keeps its own hue.
  .prose, .prose * { color: var(--ink-strong) !important; }
  // ...but links stay link-styled (Telegram-blue + underlined) despite the force.
  .prose a, .prose a * { color: #2563eb !important; text-decoration: underline !important; }
}

// Mobile: inset both bubble sides so neither spans edge-to-edge — incoming gets
// right space, outgoing gets left space, so they read as roughly level (Telegram).
@media (max-width: 768px) {
  :deep(.message-bubble-container.justify-start .left-bubble)  { margin-right: 40px; }
  :deep(.message-bubble-container.justify-end   .right-bubble) { margin-left:  40px; }
}

// ============================================================================
// Reply box
// ============================================================================
.fca-reply-wrap {
  flex-shrink: 0;
  border-top: 1px solid var(--border-soft);
  background: var(--bg-panel);
}
.fca-reply-banner {
  display: flex; align-items: center; gap: 8px;
  padding: 7px 14px 0;
}
.fca-reply-banner-bar {
  width: 3px; align-self: stretch; min-height: 26px;
  border-radius: 2px;
  background: var(--accent, #0d9488);
  flex-shrink: 0;
}
.fca-reply-banner-body { flex: 1; min-width: 0; }
.fca-reply-banner-label {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  color: var(--accent-strong, #0d9488);
  letter-spacing: 0.02em;
}
// Edit-banner variant: amber accent (#d97706) for the side-bar + label so
// it's visually distinct from the teal Reply banner — keeps agents from
// confusing edit-an-existing-message with reply-to-another-message.
.fca-reply-banner--edit {
  .fca-reply-banner-bar  { background: #d97706; }
  .fca-reply-banner-label { color: #b45309; }
}
.fca-reply-banner-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: var(--ink-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fca-reply-banner-x {
  flex-shrink: 0;
  width: 22px; height: 22px;
  border: none; background: transparent;
  color: var(--ink-muted); font-size: 18px; line-height: 1;
  cursor: pointer; border-radius: 5px;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}
.fca-attachments {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 8px 14px 0;
}
.fca-attach-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 6px;
  border-radius: 8px;
  background: var(--accent-tint);
  border: 1px solid var(--accent-ring);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 500;
  max-width: 280px;
  &--voice {
    background: #ede9fe;            // light purple — distinguishes voice intent
    border-color: #c4b5fd;
    color: #5b21b6;
  }
}
.fca-attach-voice-tag {
  font-family: 'Inter', sans-serif;
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: #5b21b6;
  color: #fff;
  padding: 1px 5px;
  border-radius: 4px;
  flex-shrink: 0;
}
.fca-attach-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1; min-width: 0;
}
.fca-attach-size {
  opacity: 0.65;
  font-size: 10.5px;
}
.fca-attach-rm {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px;
  border-radius: 50%;
  border: none;
  background: rgba(13,148,136,0.18);
  color: var(--accent-strong);
  font-size: 13px;
  line-height: 1;
  padding: 0 !important;
  cursor: pointer;
  &:hover { background: var(--accent); color: #fff; }
}
// Toolbar row above textarea
.fca-toolbar-row {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px 5px;
  position: relative;   /* containing block for the mobile link popover */
}
.fca-tool-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 5px;
  padding: 3px 8px !important;
  border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover, &.is-active {
    background: var(--bg-panel);
    color: var(--ink-strong);
    border-color: var(--border-medium);
  }
  svg { flex-shrink: 0; }
}

// Input row: textarea + send
.fca-input-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding: 0 14px 12px;
}
.fca-compose-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  background: var(--bg-main);
  overflow: hidden;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:focus-within {
    border-color: var(--accent-ring);
    box-shadow: 0 0 0 2px var(--accent-tint);
  }
}
// Footer-row INSIDE the wrap: AI button (left) + counter (right)
.fca-compose-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 7px;
  border-top: 1px dashed var(--border-soft);
  background: var(--bg-main);
}
.fca-char-counter {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
  flex-shrink: 0;
}
.fca-ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px !important;
  border-radius: 6px;
  border: 1px solid var(--accent-ring);
  background: linear-gradient(135deg, var(--accent-tint), rgba(94, 234, 212, 0.18));
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  &:hover:not(:disabled) {
    background: var(--accent);
    color: #fff;
    border-color: var(--accent);
  }
  &:disabled { opacity: 0.65; cursor: progress; }
}

// Send button stack
.fca-send-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-self: stretch;
}

// Emoji / GIF picker
.fca-emoji-picker {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(31, 42, 42, 0.13);
  padding: 12px;
  z-index: 300;
  width: 320px;
}

// Insert-link popover (URL toolbar button) — mirrors the emoji picker: opens
// upward, frosted panel. Capped to the viewport so it can't scroll the page on
// a narrow phone.
/* URL-button wrapper = positioning context for the link popover on desktop.
   On mobile it's switched to `static` (see the mobile media block) so the
   popover anchors to the full-width toolbar row instead. Position lives in the
   class (NOT inline) so the mobile override can actually win. */
.fca-url-wrap { position: relative; }
.fca-link-pop {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(31, 42, 42, 0.13);
  padding: 12px;
  z-index: 300;
  width: 260px;
  max-width: calc(100vw - 24px);
}
.fca-link-label {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.fca-link-input {
  width: 100%;
  padding: 7px 9px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--bg-subtle);
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  outline: none;
  &:focus { border-color: var(--accent); }
}
.fca-link-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
.fca-link-cancel,
.fca-link-insert {
  padding: 6px 12px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--border-soft);
}
.fca-link-cancel {
  background: var(--bg-subtle);
  color: var(--ink-muted);
}
.fca-link-insert {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.fca-emoji-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
}
.fca-emoji-tab {
  flex: 1;
  padding: 4px 0;
  border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  cursor: pointer;
  &.active {
    background: var(--accent-tint);
    color: var(--accent-strong);
    border-color: var(--accent-ring);
  }
}
.fca-emoji-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.fca-emoji-btn {
  width: 32px; height: 32px;
  border-radius: 7px;
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  padding: 0 !important;
  &:hover { background: var(--bg-subtle); }
}
.fca-gif-empty {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  text-align: center;
  padding: 24px 8px;
  line-height: 1.5;
  small { font-size: 11px; opacity: 0.7; }
}
.fca-gif-pane {
  width: 300px;
  max-width: 80vw;
}
.fca-gif-search {
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  margin-bottom: 8px;
  &:focus { outline: none; border-color: var(--accent); }
}
.fca-gif-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
  max-height: 260px;
  overflow-y: auto;
}
.fca-gif-item {
  border: none;
  padding: 0;
  background: var(--bg-subtle);
  border-radius: 7px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1 / 1;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  &:hover { outline: 2px solid var(--accent); }
}
.fca-gif-attrib {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  color: var(--ink-muted);
  text-align: right;
  margin-top: 6px;
  letter-spacing: 0.04em;
}

// AI suggestions row
.fca-ai-suggestions {
  padding: 8px 14px 2px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fca-ai-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--accent-strong);
}
.fca-ai-dismiss {
  border: none;
  background: transparent;
  color: var(--ink-muted);
  font-size: 14px;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  padding: 0 !important;
  cursor: pointer;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}
.fca-ai-card {
  display: flex;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 9px;
  border: 1px solid var(--accent-ring);
  background: var(--accent-tint);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: var(--accent);
    color: #fff;
    .fca-ai-tone, .fca-ai-text { color: #fff; }
  }
}
.fca-ai-tone {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-strong);
  flex-shrink: 0;
  padding-top: 1px;
  min-width: 50px;
}
.fca-ai-text {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-strong);
  line-height: 1.4;
}
.fca-reply-input {
  width: 100%;
  box-sizing: border-box;
  resize: none;
  flex: 1;                       // fill the compose-wrap above the footer
  min-height: 60px;
  max-height: 220px;
  padding: 9px 12px;
  border: none;                  // border lives on .fca-compose-wrap now
  background: transparent;
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  outline: none;
  &::placeholder { color: var(--ink-muted); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.fca-send-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0 !important;
  border-radius: 10px;
  border: none;
  background: var(--accent);
  color: #fff;
  cursor: pointer;
  transition: background 0.12s, transform 0.12s;
  &:hover:not(:disabled) {
    background: var(--accent-strong);
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:disabled {
    background: var(--bg-subtle);
    color: var(--ink-muted);
    cursor: not-allowed;
  }
  // Edit-mode variant: amber to match the editing-banner accent so the agent
  // sees one consistent "warning, this isn't a fresh send" color thread.
  &--edit {
    background: #d97706;
    &:hover:not(:disabled) { background: #b45309; }
  }
}
.fca-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: fca-spin 0.7s linear infinite;
  &--dark {
    width: 12px; height: 12px;
    border: 2px solid var(--border-soft);
    border-top-color: var(--accent-strong);
  }
}
@keyframes fca-spin {
  to { transform: rotate(360deg); }
}

/* ── Mobile (≤768px): condense the chat header to the clean mockup look ──
   The "Show Notes" text + "+ Label" button are hidden via :isMobile in the
   template; here we tighten spacing, square-off the now icon-only Notes
   button, and truncate the contact name so the action icons never get
   pushed off-screen on a ~390px phone. Desktop is untouched. */
@media (max-width: 768px) {
  .fca-header { padding-left: 8px; padding-right: 8px; gap: 7px; }
  .fca-name {
    display: inline-block;
    max-width: 44vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }
  .fca-notes-btn { padding: 7px !important; gap: 0; }
  .fca-fg-pill {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    padding: 0 10px;
    border: 1px solid rgba(91, 46, 207, 0.22); /* subtle Fangate-purple-tinted frame */
    border-radius: 9px;
    background: #f3f2f6; /* light grey surface — the gradient wordmark carries the brand */
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(31, 42, 42, 0.07);
    transition: border-color 0.12s, background 0.12s;
  }
  .fca-fg-pill:hover { border-color: rgba(91, 46, 207, 0.42); background: #efeef4; }
  .fca-fg-wordmark { height: 15px; width: auto; display: block; }
  /* Search trigger — floats at the top-left, just under the header, roughly in
     line with the centered persona pill. Anchored to the chat-area root
     (position:relative), NOT the persona overlay (which is pointer-events:none,
     hence the old in-overlay trigger was dead). Search + ⋮ are removed from the
     header via v-if="!isMobile" (no CSS battle with .fca-action's !important). */
  .fca-msearch-trigger {
    position: absolute;
    top: 80px;
    left: 10px;
    z-index: 6;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 1px solid var(--border-soft);
    border-radius: 9px;
    background: var(--bg-panel, #fff);
    color: var(--ink-strong);
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(31, 42, 42, 0.08);
  }
  /* Tap feedback — header buttons scale slightly on press (mobile has no hover). */
  .fca-back, .fca-notes-btn, .fca-action, .fca-fg-pill, .fca-msearch-trigger { transition: transform 0.08s; }
  .fca-back:active, .fca-notes-btn:active, .fca-action:active, .fca-fg-pill:active, .fca-msearch-trigger:active { transform: scale(0.9); }

  /* Rounded "pill" compose input — white, soft shadow, borderless (modern). */
  .fca-compose-wrap {
    border-radius: 22px;
    border-color: transparent;
    background: var(--bg-panel);
    box-shadow: 0 1px 2px rgba(20, 22, 40, 0.05), 0 2px 8px rgba(20, 22, 40, 0.04);
  }
  /* URL/link popover: the button-anchored position (left:0 from a mid-toolbar
     button) pushed it off the right edge on a phone. Neutralise the button
     wrapper so the popover anchors to the full-width toolbar row instead — then
     it sits flush above the chat input field and is clamped to both edges, so
     it can't overflow the screen. */
  .fca-url-wrap { position: static; }
  .fca-link-pop {
    left: 14px;
    right: 14px;
    width: auto;
    max-width: none;
  }
}
</style>

<!--
  Non-scoped global style block — Vue's scoped + :deep() doesn't always
  reach Chatwoot's deeply-nested next/message/* tree (component boundaries
  are crossed multiple times). This block targets the message-action
  trigger by Chatwoot's class signature and forces it always-visible.
  Scoped to .fangate-chat-area to keep blast radius minimal.
-->
<style lang="scss">
.fangate-chat-area {
  // Force Chatwoot's hover-revealed kebab button to always be visible on
  // every bubble. The button has Tailwind's `invisible` utility plus
  // `group-hover/context-menu:visible` — we override the invisible.
  button.invisible {
    visibility: visible !important;
    opacity: 0.55 !important;
    transition: opacity 0.15s, transform 0.1s !important;
    &:hover { opacity: 1 !important; transform: translateY(1px); }
  }
}

// Brief flash when we jump to a message (search hit / reply-context click).
// Unscoped on purpose: the target row (id="message{id}") is rendered by the
// child <MessageList>, outside this component's scoped-style reach.
.fca-flash {
  animation: fca-flash-kf 2.1s ease-out;
  border-radius: 12px;
}
@keyframes fca-flash-kf {
  0%, 25%  { background-color: rgba(56, 189, 248, 0.22); }
  100%     { background-color: transparent; }
}
</style>
